import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Tooltip,
  Chip,
  keyframes,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltIcon from "@mui/icons-material/Bolt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ChatMarkdown from "./ChatMarkdown";
import { mapCasesResponse } from "../../utilities/serviceMappings";
import ReactECharts from "echarts-for-react";
import Swal from "sweetalert2";
import {
  FaCartShopping,
  FaFileLines,
  FaRegLightbulb,
  FaCircleCheck,
  FaCircleXmark,
  FaRegThumbsUp,
  FaThumbsUp,
  FaRegThumbsDown,
  FaThumbsDown,
} from "react-icons/fa6";
import { useSite } from "../../context/siteContext";
import { useCart } from "../../context/cartContext";
import { getProductsById } from "../../services/cartService";
import { getDocumentSasUrl } from "../../services/documentService";
import { getSiteAddressesNew } from "../../services/manageSitesService";
import { createOrder } from "../../services/checkoutService";
import { useAuth } from "../../context/authContext";
import {
  askChatbot,
  clearChatbotSession,
  getChatbotStatus,
  submitChatbotFeedback,
  streamChatbot,
} from "../../services/chatBotService";
import TopProductListItem from "../TopProductListItem/TopProductListItem";
import MachineListItem from "../MachineListItem/MachineListItem";
import DocCard from "../DocCard/DocCard";
import DocumentViewModal from "../DocumentViewModal/DocumentViewModal";
import { ROUTES } from "../../constants";
import { trackChipUx } from "./chipAnalytics";
import "./ChatBot.css";
import { captureFeedbackContext, feedbackPayload, flushFeedbackBeforeRetry } from "./feedbackRetry";

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

// English canonical text for every stage/suggestion string below doubles as the i18next
// `defaultValue` (see STAGE_KEYS/SUGGESTION_KEYS + tSuggestion/tStage), so the UI still
// reads correctly in English even before a translation for a given key exists in
// src/locales/<lng>/translation.json.
const STAGE_MESSAGES = [
  "Looking into it...",
  "Checking your account details...",
  "Finding the right information...",
  "Preparing your answer...",
  "Almost done...",
];
// Keys under CHAT_SUPPORT.STAGE_MESSAGES.* — same array index as STAGE_MESSAGES above.
const STAGE_KEYS = [
  "LOOKING_INTO_IT",
  "CHECKING_ACCOUNT_DETAILS",
  "FINDING_INFORMATION",
  "PREPARING_ANSWER",
  "ALMOST_DONE",
];
// Translates one STAGE_MESSAGES entry via its CHAT_SUPPORT.STAGE_MESSAGES.<KEY> i18next key,
// falling back to the English text itself when no `t` function (or no translation yet) is
// available — mirrors the tSuggestion() pattern used for the initial suggested questions below.
const tStage = (index, t) => {
  const en = STAGE_MESSAGES[index];
  const key = STAGE_KEYS[index];
  return key && typeof t === "function" ? t(`CHAT_SUPPORT.STAGE_MESSAGES.${key}`, en) : en;
};

const DEFAULT_QUESTIONS = [
  "Add my most ordered parts to cart",
  "Show my recent order summary",
  "Show my request list",
  "Track my latest order",
  "Show top ordered parts",
  "Show relevant documents",
];
const CHIP_ICON = "/chip-icon.png";

// Every suggested-question string that can appear from DEFAULT_QUESTIONS or
// getInitialQuestionsForPath below, mapped to a key under CHAT_SUPPORT.SUGGESTIONS.* — the
// SAME translation-key pattern already used elsewhere in this file (see t("CHAT_SUPPORT...")
// and t("UI_TEXT...") calls) rather than inventing a new one. Suggested-question text doubles
// as both the visible chip label AND the literal chat message sent to CHIP when clicked
// (see handleSuggestedQuestion) — translating it here means a French-locale user both sees
// and asks these in French, and the backend's own language directive (system_prompt.js)
// keeps the reply in the same language regardless.
const SUGGESTION_KEYS = {
  "Add my most ordered parts to cart": "ADD_MOST_ORDERED_PARTS_TO_CART",
  "Show my recent order summary": "SHOW_RECENT_ORDER_SUMMARY",
  "Show my request list": "SHOW_REQUEST_LIST",
  "Track my latest order": "TRACK_LATEST_ORDER",
  "Show top ordered parts": "SHOW_TOP_ORDERED_PARTS",
  "Show relevant documents": "SHOW_RELEVANT_DOCUMENTS",
  "Browse parts for my site": "BROWSE_PARTS_FOR_MY_SITE",
  "Find parts compatible with my machine": "FIND_PARTS_COMPATIBLE_WITH_MY_MACHINE",
  "Show my cart summary": "SHOW_CART_SUMMARY",
  "Move unavailable parts to request list": "MOVE_UNAVAILABLE_PARTS_TO_REQUEST_LIST",
  "Recommend complementary parts for my cart": "RECOMMEND_COMPLEMENTARY_PARTS_FOR_MY_CART",
  "Update quantities in my cart": "UPDATE_QUANTITIES_IN_MY_CART",
  "Prepare this cart for checkout": "PREPARE_THIS_CART_FOR_CHECKOUT",
  "Show my request list summary": "SHOW_REQUEST_LIST_SUMMARY",
  "Move approved request list items to cart": "MOVE_APPROVED_REQUEST_LIST_ITEMS_TO_CART",
  "Create a request list from my recent order": "CREATE_REQUEST_LIST_FROM_RECENT_ORDER",
  "Show request list items by machine": "SHOW_REQUEST_LIST_ITEMS_BY_MACHINE",
  "Prioritize urgent request list items": "PRIORITIZE_URGENT_REQUEST_LIST_ITEMS",
  "What items are waiting for quotation?": "WHAT_ITEMS_ARE_WAITING_FOR_QUOTATION",
  "Show my latest order status": "SHOW_LATEST_ORDER_STATUS",
  "Reorder all parts from my last order": "REORDER_ALL_PARTS_FROM_LAST_ORDER",
  "Show top ordered parts in the last 90 days": "SHOW_TOP_ORDERED_PARTS_LAST_90_DAYS",
  "Track shipment for my open orders": "TRACK_SHIPMENT_FOR_OPEN_ORDERS",
  "Show delayed order lines": "SHOW_DELAYED_ORDER_LINES",
  "Create a quick reorder list": "CREATE_QUICK_REORDER_LIST",
  "Search for a specific part": "SEARCH_FOR_SPECIFIC_PART",
  "Show me my top ordered parts": "SHOW_ME_MY_TOP_ORDERED_PARTS",
  "Check if a part is purchasable": "CHECK_IF_PART_IS_PURCHASABLE",
  "Browse available parts for my site": "BROWSE_AVAILABLE_PARTS_FOR_MY_SITE",
  "Recommend parts for my machines": "RECOMMEND_PARTS_FOR_MY_MACHINES",
  "Show me the BOMs": "SHOW_ME_THE_BOMS",
  "Show mechanical drawings": "SHOW_MECHANICAL_DRAWINGS",
  "Show electrical drawings": "SHOW_ELECTRICAL_DRAWINGS",
  "Show compliance documents": "SHOW_COMPLIANCE_DOCUMENTS",
  "Show relevant documents for my site": "SHOW_RELEVANT_DOCUMENTS_FOR_MY_SITE",
};
// Translates one suggested-question string via CHAT_SUPPORT.SUGGESTIONS.<KEY>, defaulting to
// the English text itself (both when no translation exists yet and when no `t` was passed —
// callers outside a render, or before i18n has initialized, still get valid English text).
const tSuggestion = (text, t) => {
  const key = SUGGESTION_KEYS[text];
  return key && typeof t === "function" ? t(`CHAT_SUPPORT.SUGGESTIONS.${key}`, text) : text;
};
const tSuggestionList = (list, t) => list.map((q) => tSuggestion(q, t));

const getInitialQuestionsForPath = (pathname = "", { hasCartItems = true } = {}, t) => {
  const path = String(pathname || "").toLowerCase();

  if (path.includes("/cart")) {
    // An empty cart can't be summarized, moved-from, recommended-against, updated, or
    // checked out — those five all presuppose existing lines, so swap in prompts that
    // still make sense with nothing in the cart yet.
    if (!hasCartItems) {
      return tSuggestionList([
        "Add my most ordered parts to cart",
        "Browse parts for my site",
        "Show my recent order summary",
        "Show my request list",
        "Show top ordered parts",
        "Find parts compatible with my machine",
      ], t);
    }
    return tSuggestionList([
      "Show my cart summary",
      "Add my most ordered parts to cart",
      "Move unavailable parts to request list",
      "Recommend complementary parts for my cart",
      "Update quantities in my cart",
      "Prepare this cart for checkout",
    ], t);
  }

  if (path.includes("/request") || path.includes("/rfq")) {
    return tSuggestionList([
      "Show my request list summary",
      "Move approved request list items to cart",
      "Create a request list from my recent order",
      "Show request list items by machine",
      "Prioritize urgent request list items",
      "What items are waiting for quotation?",
    ], t);
  }

  if (path.includes("/order")) {
    return tSuggestionList([
      "Show my latest order status",
      "Reorder all parts from my last order",
      "Show top ordered parts in the last 90 days",
      "Track shipment for my open orders",
      "Show delayed order lines",
      "Create a quick reorder list",
    ], t);
  }

  if (path.includes("/search") || path.includes("/product")) {
    // Previously "...this part"/"...my selected machine" — no part/machine is actually
    // selected at this entry point (same class of bug fixed for the /doc list above).
    // Generic actions here still resolve against the current site automatically via
    // screen_context once sent, same as every other suggestion in this file.
    return tSuggestionList([
      "Search for a specific part",
      "Show me my top ordered parts",
      "Check if a part is purchasable",
      "Browse available parts for my site",
      "Recommend parts for my machines",
    ], t);
  }

  if (path.includes("/doc") || path.includes("/knowledge")) {
    // Previously included "Find installation guide for this part" and "Open the latest
    // datasheet" — both presuppose a specific part/document that was never selected at
    // this entry point, and live-reproduced the model fabricating a fake part number and
    // fake site-availability data to answer them rather than asking what part was meant.
    // Replaced with real, always-valid category browsing (confirmed working categories).
    return tSuggestionList([
      "Show me the BOMs",
      "Show mechanical drawings",
      "Show electrical drawings",
      "Show compliance documents",
      "Show relevant documents for my site",
    ], t);
  }

  return tSuggestionList(DEFAULT_QUESTIONS, t);
};

// `attachment_type` on a document row is the file EXTENSION (pdf, xlsx, dwg, ...),
// never a storage-method indicator — and `attachment` is always already a full,
// absolute URL (Azure Blob Storage), never a server-relative path. Confirmed against
// the real documents table: previously this checked for attachment_type === "url"/"file",
// values that never actually occur, so every document silently got href="" and rendered
// as inert text regardless of what real data existed.
const getDocHref = (doc) => (doc?.attachment ? String(doc.attachment) : "");

const getDocChipLabel = (doc) => {
  const ext = String(doc?.attachment_type || "").toLowerCase();
  if (!ext) return "DOC";
  const map = {
    pdf: "PDF",
    png: "PNG",
    jpg: "JPG",
    jpeg: "JPG",
    csv: "CSV",
    xls: "Excel",
    xlsx: "Excel",
    xlsm: "Excel",
    doc: "Word",
    docx: "Word",
    ppt: "PPT",
    pptx: "PPT",
    dwg: "DWG",
  };
  return map[ext] || ext.toUpperCase();
};

const normalizeRequestRowToProduct = (row) => {
  if (!row) return null;
  if (row?.productData) return row.productData;
  if (row?.product_id && (row?.product_name || row?.product_number)) return row;
  return null;
};

const normalizeRequestRow = (row, bucket) => {
  if (!row) return null;
  const product = normalizeRequestRowToProduct(row);
  if (!product) return null;
  const machineId = Number(row?.machine_id || row?.machineData?.machine_id || product?.machine_id || 0) || null;
  const machineName = row?.machineData?.machine_name || product?.machine_name || "";
  const machineNumber = row?.machineData?.machine_number || product?.machine_number || "";
  return {
    bucket,
    machine_id: machineId,
    machine_name: machineName,
    machine_number: machineNumber,
    product,
  };
};

const inferActionType = (action = {}) => {
  const explicit = String(action?.action_type || action?.type || "").toLowerCase();
  if (explicit === "agentic" || explicit === "question") return explicit;

  const prompt = String(action?.prompt || "").toLowerCase();
  if (!prompt) return "question";
  if (prompt === "yes" || prompt === "no") return "agentic";
  if (/^(show|what|which|where|when|why|how|list|find|track|open)\b/.test(prompt)) return "question";
  if (/\?$/.test(prompt)) return "question";
  return "agentic";
};

const GENERIC_ACTION_TEXT = new Set([
  "ask",
  "action",
  "question",
  "questions",
  "actions",
]);

const normalizeAssistantActions = (rawActions = []) => {
  const seen = new Set();
  return (Array.isArray(rawActions) ? rawActions : [])
    .map((raw) => (typeof raw === "string" ? { prompt: raw } : raw))
    .filter(Boolean)
    .map((act) => {
      const label = String(act?.label || "").trim();
      const prompt = String(act?.prompt || "").trim();
      const path = String(act?.path || act?.route || "").trim();
      return { ...act, label, prompt, path };
    })
    .filter((act) => {
      if (!act.prompt && !act.label && !act.path) return false;
      const labelLower = act.label.toLowerCase();
      const promptLower = act.prompt.toLowerCase();
      if (GENERIC_ACTION_TEXT.has(labelLower) && !act.prompt && !act.path) return false;
      if (GENERIC_ACTION_TEXT.has(promptLower) && !act.label && !act.path) return false;
      return true;
    })
    .filter((act) => {
      const key = `${inferActionType(act)}|${act.label.toLowerCase()}|${act.prompt.toLowerCase()}|${act.path.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
};

// All branches render at the same explicit 15px regardless of icon source — mixing
// react-icons/fa6 (previously a hardcoded size={14}) with MUI icons (previously
// fontSize="small", which resolves to 20px) made icons on adjacent action chips
// visibly different sizes depending on which branch matched a given chip's prompt.
const ACTION_ICON_SIZE = 15;
const muiIconSx = { fontSize: ACTION_ICON_SIZE };

const getIconForPrompt = (text = "", type = "question") => {
  const prompt = String(text || "").toLowerCase();
  if (prompt === "yes" || prompt.includes("confirm")) return <FaCircleCheck size={ACTION_ICON_SIZE} />;
  if (prompt === "no" || prompt.includes("cancel")) return <FaCircleXmark size={ACTION_ICON_SIZE} />;
  if (prompt.includes("cart") || prompt.includes("checkout")) return <FaCartShopping size={ACTION_ICON_SIZE} />;
  if (prompt.includes("request list") || prompt.includes("request")) return <RequestQuoteIcon sx={muiIconSx} />;
  if (prompt.includes("order") || prompt.includes("track") || prompt.includes("shipment")) return <LocalShippingIcon sx={muiIconSx} />;
  if (prompt.includes("support") || prompt.includes("case")) return <SupportAgentIcon sx={muiIconSx} />;
  if (prompt.includes("document") || prompt.includes("manual") || prompt.includes("knowledge")) return <FaFileLines size={ACTION_ICON_SIZE} />;
  if (prompt.includes("recommend")) return <FaRegLightbulb size={ACTION_ICON_SIZE} />;
  if (prompt.includes("part") || prompt.includes("product") || prompt.includes("machine")) return <Inventory2Icon sx={muiIconSx} />;
  return type === "agentic" ? <BoltIcon sx={muiIconSx} /> : <HelpOutlineIcon sx={muiIconSx} />;
};

const getChartOption = (chart = {}) => {
  const x = Array.isArray(chart?.x) ? chart.x : [];
  const series = Array.isArray(chart?.series) ? chart.series : [];
  const isBar = String(chart?.type || "").toLowerCase() === "bar";
  const currency = String(chart?.currency || "USD");

  return {
    grid: { left: 44, right: 18, top: 34, bottom: 30 },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value) => {
        if (chart?.y_format === "currency") return `${currency} ${Number(value || 0).toLocaleString()}`;
        return Number(value || 0).toLocaleString();
      },
    },
    xAxis: { type: "category", data: x, axisLabel: { fontSize: 10 } },
    yAxis: { type: "value", axisLabel: { fontSize: 10 } },
    series: series.map((s) => ({
      name: s?.name || "Series",
      type: isBar ? "bar" : "line",
      smooth: !isBar,
      data: Array.isArray(s?.data) ? s.data : [],
      areaStyle: isBar ? undefined : { opacity: 0.15 },
    })),
    legend: { top: 0, textStyle: { fontSize: 11 } },
  };
};

const formatShortDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours24 = d.getHours();
  const hours = String(hours24 % 12 || 12).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${month}-${day}-${d.getFullYear()} ${hours}:${minutes}:${seconds} ${hours24 >= 12 ? "PM" : "AM"}`;
};

// Chat message timestamp label: today -> time only, yesterday -> "Yesterday <time>",
// older -> "<date> <time>". Day boundaries are compared by calendar date (local), not a
// rolling 24h window, so a message from 11pm last night reads "Yesterday", not "today".
const formatChatTimestamp = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (dayDiff <= 0) return time; // today (or a clock-skew future stamp) -> time only, no "Today"
  if (dayDiff === 1) return `Yesterday ${time}`;
  const date = d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  return `${date} ${time}`;
};

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const prettifyKey = (key) =>
  String(key || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const CURRENCY_SYMBOLS = { usd: "$", gbp: "£", eur: "€", cad: "C$", inr: "₹", sgd: "S$" };

// Part price in the SITE's currency (price_<ccy>). Falls back to product_sell_price. Returns
// "—" when the value is 0/absent — many dev-DB parts carry no price, and £0.00 on a real part
// would read as "free" rather than "unpriced".
// Live-reported: a non-purchasable part (e.g. pricing not yet updated on its own product page)
// still showed a real-looking price in CHIP's chat table right next to "Purchasable: No" —
// misleading, since that price isn't actually available to pay. Mirrors the real /rspl page's
// own rule (Rspl.js: `purchasable ? currencyPrice : 0`), which already hides price the same way.
const formatPartPrice = (r, currency) => {
  const isPurchasable = Number(r?.purchasable) === 1 || r?.purchasable === true;
  if (!isPurchasable) return "—";
  const c = String(currency || "usd").toLowerCase();
  const v = Number(r?.[`price_${c}`] ?? r?.product_sell_price ?? 0);
  if (!Number.isFinite(v) || v <= 0) return "—";
  return `${CURRENCY_SYMBOLS[c] || ""}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Column specs per recognized data array. One compact table is rendered per assistant turn:
// the first spec whose array is present and non-empty wins (see resolveDataTable). When
// rowPrompt returns a string, the row is clickable and routes through handleActionClick — the
// same path every action/data chip uses — so navigation reuses the existing agent_actions
// mechanism rather than inventing a new one. Column get() receives (row, ctx) where ctx.currency
// is the active site currency (for price formatting).
const DATA_TABLE_SPECS = [
  {
    key: "workorders",
    title: "Work Orders",
    columns: [
      { label: "WO #", get: (r) => r.wo_number },
      { label: "Type", get: (r) => r.wo_type },
      { label: "Status", get: (r) => r.wo_status },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Service date", get: (r) => formatShortDate(r.service_start_date) },
    ],
    rowPrompt: (r) => (r.wo_number ? `View details for WO ${r.wo_number}` : null),
  },
  {
    key: "orders",
    title: "Orders",
    columns: [
      { label: "Order #", get: (r) => r.order_number },
      { label: "Status", get: (r) => r.order_status },
      { label: "Created", get: (r) => formatShortDate(r.order_created_date) },
      { label: "Items", get: (r) => (Array.isArray(r.products) ? r.products.length : null) },
    ],
    rowPrompt: (r) => (r.order_number ? `Show details for order ${r.order_number}` : null),
  },
  {
    key: "products",
    title: "Parts",
    columns: [
      { label: "Product Number", get: (r) => r.product_number },
      { label: "Product Name", get: (r) => r.product_name },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Machine Name", get: (r) => r.machine_name },
      { label: "Purchasable", get: (r) => (r.purchasable === undefined || r.purchasable === null ? "—" : (r.purchasable ? "Cart" : "Request")) },
      { label: "Price", get: (r, ctx) => formatPartPrice(r, ctx?.currency) },
    ],
    rowPrompt: (r) => (r.product_number ? `Show me part ${r.product_number}` : null),
  },
  {
    key: "parts",
    title: "Parts",
    columns: [
      { label: "Product Number", get: (r) => r.product_number },
      { label: "Product Name", get: (r) => r.product_name },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Machine Name", get: (r) => r.machine_name },
      { label: "Purchasable", get: (r) => (r.purchasable === undefined || r.purchasable === null ? "—" : (r.purchasable ? "Cart" : "Request")) },
      { label: "Price", get: (r, ctx) => formatPartPrice(r, ctx?.currency) },
    ],
    rowPrompt: (r) => (r.product_number ? `Show me part ${r.product_number}` : null),
  },
  {
    // Product-direction fix 2026-07-28: was checked BEFORE "products"/"parts" in this array —
    // resolveDataTable renders the first NON-EMPTY array it finds, and search_products
    // (catalog.tools.js) always returns BOTH `products` and `machines` together (its underlying
    // searchProductsAndMachines query matches products AND machines in one pass). Live-reported:
    // "part number starts with 1000" correctly found and described a PART in the answer text
    // (100072722, "BP, INFEED CONV NOSE BAR"), but a real machine number (21686-1000) also
    // literally contains "1000" — a genuine coincidental match, not a bug in that tool — and with
    // "machines" checked first, the rendered card showed THAT machine instead of the part the
    // answer was actually about. Every OTHER tool that returns a `machines` array (get_machine_list,
    // any ambiguous-machine picker across viewer/documents/requestlist/catalog tools) does so
    // BEFORE any products/parts data exists at all (they're asking "which machine?", not showing
    // parts yet) — so moving this below products/parts changes nothing for those; it only changes
    // which table wins when BOTH are genuinely present, which should be the part actually asked
    // about, not an incidental machine-number collision.
    key: "favorites",
    title: "Favorites",
    columns: [
      { label: "Document Name", get: (r) => r.name },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Machine Name", get: (r) => r.machine_name },
      { label: "Favorited At", get: (r) => formatDateTime(r.favorited_at) },
    ],
    rowPrompt: null,
  },
  {
    key: "suggestions",
    title: "Suggestions",
    columns: [
      { label: "Product Number", get: (r) => r.product_number },
      { label: "Product Name", get: (r) => r.product_name },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Machine Name", get: (r) => r.machine_name },
    ],
    rowPrompt: (r) => (r.product_number ? `Show me part ${r.product_number}` : null),
  },
  {
    key: "part_matches",
    title: "Part Matches",
    columns: [
      { label: "Bakery Location Number", get: (r) => r.customer_site_number },
      { label: "Bakery Location Name", get: (r) => r.customer_site_name },
      { label: "Machine Number", get: (r) => r.machine_number },
      { label: "Machine Name", get: (r) => r.machine_name },
      { label: "Product Number", get: (r) => r.product_number },
      { label: "Product Name", get: (r) => r.product_name },
    ],
    rowPrompt: (r) => (r.product_number ? `Show me part ${r.product_number}` : null),
  },
  {
    key: "machines",
    title: "Machines",
    columns: [
      { label: "Machine #", get: (r) => r.machine_number },
      { label: "Name", get: (r) => r.machine_name },
      { label: "Status", get: (r) => r.machine_status },
    ],
    rowPrompt: (r) => (r.machine_number ? `Show details for machine ${r.machine_number}` : null),
  },
  {
    key: "cases",
    title: "Cases",
    columns: [
      // Rows are pre-mapped through mapCasesResponse in resolveDataTable, so case_status /
      // case_priority already carry labels ("Closed", "High") instead of numeric CRM codes.
      { label: "Case #", get: (r) => r.case_number || r.number },
      { label: "Type", get: (r) => r.case_type || r.type },
      { label: "Status", get: (r) => r.case_status || r.status },
      { label: "Priority", get: (r) => r.case_priority || r.priority },
    ],
    // Live-reported 2026-07-22: cases were the only DATA_TABLE_SPECS entry with rowPrompt
    // hardcoded to null, so a rendered Cases table (e.g. an ambiguous-match pick-list) was
    // never clickable — unlike every other entity here (workorders/orders/machines/parts/
    // sites all resend a message on row click). "Show case X" matches this tool's own
    // documented case_number phrasing and orchestrator.js's CASE_NUMBER_RE deterministic
    // override, so a click deep-links straight to that case.
    rowPrompt: (r) => {
      const num = r.case_number || r.number;
      return num ? `Show case ${num}` : null;
    },
  },
  {
    key: "sites",
    title: "Locations",
    columns: [
      { label: "Bakery Location Number", get: (r) => r.site_number || r.customer_site_number },
      { label: "Bakery Location Name", get: (r) => r.site_name || r.customer_site_name },
    ],
    rowPrompt: (r) => {
      const num = r.site_number || r.customer_site_number;
      return num ? `Switch to site ${num}` : null;
    },
  },
  {
    // AMF-staff-only CCR review queue (get_ccr_recommendation_queue, part_engine.tools.js).
    // Named `queue_items` on the backend specifically so it doesn't collide with the generic
    // `items` key several other tools (cart, request list, decide_ccr_recommendations_bulk)
    // also use for differently-shaped rows — previously this data reached the frontend but
    // never rendered at all (GENERIC_TABLE_SKIP_KEYS deliberately blocks bare `items`).
    key: "queue_items",
    title: "Review Queue",
    columns: [
      { label: "Status", get: (r) => r.status },
      { label: "Part", get: (r) => r.description || r.product_number },
      { label: "Site", get: (r) => r.site_number },
      { label: "Reason", get: (r) => r.reason },
    ],
    // No rowPrompt: there's no "show me recommendation #N" tool to send a click to — deciding
    // an id is already handled via the SHORTCUTS/chip prompts CHIP itself offers per row
    // ("Approve recommendation N"), same reasoning as work orders/report links having no
    // create-flow chip when no tool backs one.
    rowPrompt: null,
  },
  {
    // RAG source citations — clicking a row opens the source manual PDF at the cited page
    // (rowHref = the backend-built <blob-url>#page=N). Internal document_id is not shown.
    key: "citations",
    title: "Sources",
    columns: [
      { label: "Manual", get: (r) => r.name },
      { label: "Page", get: (r) => (r.page != null && r.page !== "" ? r.page : "—") },
    ],
    rowPrompt: null,
    rowHref: (r) => r.url || null,
  },
];

// A row-click's identity field, keyed by which DATA_TABLE_SPECS table it belongs to — see the
// matchingAction/rowId logic below (in the table-row render) for why this must be table-aware
// rather than a blind OR-chain across every possible id field name.
const ROW_ID_FIELD_BY_TABLE_KEY = {
  suggestions: (r) => r?.product_number,
  part_matches: (r) => r?.product_number,
  machines: (r) => r?.machine_number,
  orders: (r) => r?.order_number,
  products: (r) => r?.product_number,
  parts: (r) => r?.product_number,
  workorders: (r) => r?.wo_number,
  cases: (r) => r?.case_number,
  // Missing here until 2026-08-31: an ambiguous site-name resolution (e.g. "show me ccr
  // recommendations for columbus bakery" matching 2 sites) renders BOTH this Locations table
  // AND per-site SHORTCUTS chips whose prompt carries the ORIGINAL intent forward (e.g. "Switch
  // to site LN-0001420 and show its CCR recommendations" — see site.tools.js's follow_up param).
  // Without a "sites" entry here, rowId was always null, matchingAction never even looked, and
  // clicking the table row instead fell through to the generic bare rowPrompt below ("Switch to
  // site LN-0001420") — same intent-loss bug this map already exists to prevent for every other
  // table (see the 2026-07-21/2026-07-28 comments above), just never extended to sites.
  sites: (r) => r?.site_number || r?.customer_site_number,
};

const GENERIC_TABLE_SKIP_KEYS = new Set([
  "workorders", "orders", "machines", "products", "parts", "part_matches", "favorites", "cases", "sites",
  "citations", "documents", "matches", "charts", "available_to_purchase", "request_list",
  "rows", "items", "agent_actions",
  // Identifier-resolution diagnostics are tool metadata, not a customer-facing table.
  "remapped",
  // A tool's own `actions` array (e.g. view_document's ambiguous-match disambiguation chips —
  // documents.tools.js) is ALREADY rendered as real clickable buttons via the "Ask"/"Shortcuts"
  // chip UI below (see the chat-actions-wrap block). Without this in the skip set, the generic
  // fallback here picked it up as an unrecognized array and rendered a raw table titled "Label
  // / Prompt / Action Type" — leaking the internal `prompt` string (the literal message sent to
  // CHIP when a chip is clicked) straight into the chat as if it were real answer data.
  // Live-reported 2026-08-10.
  "actions",
]);

// Fallback for an array shape we don't have a spec for: render the first ~5 scalar keys of
// its first row as a generic table so unexpected list data still shows compactly.
const buildGenericDataTable = (data) => {
  const INTERNAL_ID_KEYS = new Set(["id", "product_id", "machine_id", "document_id", "site_id", "user_id"]);
  for (const [key, value] of Object.entries(data || {})) {
    if (key.startsWith("_") || GENERIC_TABLE_SKIP_KEYS.has(key)) continue;
    if (!Array.isArray(value) || !value.length) continue;
    const first = value[0];
    if (!first || typeof first !== "object" || Array.isArray(first)) continue;
    const scalarKeys = Object.keys(first)
      .filter((k) => {
        if (INTERNAL_ID_KEYS.has(k) || k.endsWith("_id")) return false;
        const v = first[k];
        return v === null || ["string", "number", "boolean"].includes(typeof v);
      })
      .slice(0, 5);
    if (!scalarKeys.length) continue;
    return {
      key,
      title: prettifyKey(key),
      columns: scalarKeys.map((k) => ({ label: prettifyKey(k), get: (r) => r[k] })),
      rows: value,
      rowPrompt: null,
    };
  }
  return null;
};

// Pick the FIRST recognized non-empty array in item.data and describe it as one table.
const resolveDataTable = (data) => {
  for (const spec of DATA_TABLE_SPECS) {
    const arr = data?.[spec.key];
    if (Array.isArray(arr) && arr.length) {
      // Cases carry numeric CRM codes (case_status/type/priority/origin) — map to labels via
      // the same table the real Cases page uses before the columns read them.
      const rows = spec.key === "cases" ? mapCasesResponse(arr) : arr;
      return { key: spec.key, title: spec.title, columns: spec.columns, rows, rowPrompt: spec.rowPrompt, rowHref: spec.rowHref };
    }
  }
  return buildGenericDataTable(data);
};

const ChatBot = ({ onClose, fullPage = false, onToggleFullPage } = {}) => {
  const { t, i18n } = useTranslation();
  const { selectedSite, selectedSiteData, siteData, updateSite } = useSite();
  const { cartItems, clearCart, refreshCart } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [docModal, setDocModal] = useState({ open: false, document: null, page: null });
  const [input, setInput] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sendMessage, setSendMessage] = useState(false);
  // One-shot override for the visible user bubble text when an action chip's label
  // differs from what's actually sent (e.g. "Stay on current site" chip sends "no") —
  // consumed once in handleSend so it never bleeds into a later typed message.
  const [pendingDisplayText, setPendingDisplayText] = useState(null);
  // Persist across a page reload/remount within the same browser tab. Previously this
  // (and conversationId, right below) were plain useState with no storage backing — the
  // conversationId half was already fixed, but the visible message bubbles were not, so
  // any full remount of ChatBot (e.g. navigating to /model-viewer or /2d-model-viewer,
  // which render as standalone routes outside DefaultLayout and so unmount ChatPopup
  // entirely) silently wiped the on-screen chat even though the backend session (keyed by
  // conversationId) was still alive and intact.
  // Chat history persists across logout/login and tabs (Copilot-style), stored in
  // localStorage keyed PER USER — so a different login only ever reads its own transcript
  // (isolation preserved, which is what the old Bug #27 sessionStorage-wipe protected). Start
  // empty; the load effect below populates the authenticated user's stored transcript.
  const chatKey = (uid, which) => `chip_conv_${which}_${uid ?? "anon"}`;
  const [conversationHistory, setConversationHistory] = useState([]);
  const [conversationId, setConversationId] = useState("");
  // Gates the persist effect so it never clobbers a user's stored chat with the empty
  // initial state before the load effect has run for that user.
  const chatLoadedForUserRef = useRef(null);

  useEffect(() => {
    const uid = user?.user_id ?? null;
    if (uid === null || chatLoadedForUserRef.current !== uid) return; // wait until loaded for this user
    try {
      // _productMap (see renderDataBlocks) is a live Map, which JSON.stringify silently
      // turns into "{}" — strip it before persisting instead of writing dead weight that
      // renderDataBlocks then has to guard against on every restored message.
      const serializable = conversationHistory.map((item) => {
        // Live-reported 2026-07-21: a query in flight at logout was persisted with
        // response === null, then restored on next login where response === null renders the
        // loading skeleton forever with no live request behind it ("query hanged"). Never
        // persist an incomplete turn as a live skeleton — settle it to a terminal string and
        // drop any pending action so it restores as a finished (stopped) turn.
        const settled = item && item.response == null
          ? { ...item, response: t("UI_TEXT.RESPONSE_STOPPED", "Response stopped."), pendingAction: null }
          : item;
        return settled?.data?._productMap
          ? { ...settled, data: { ...settled.data, _productMap: undefined } }
          : settled;
      });
      localStorage.setItem(chatKey(uid, "history"), JSON.stringify(serializable));
    } catch (_err) {
      // localStorage unavailable/quota exceeded — graceful no-op; chat just won't persist.
    }
  }, [conversationHistory, user?.user_id]);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);
  const [initialQuestions, setInitialQuestions] = useState(() =>
    getInitialQuestionsForPath(location?.pathname || window.location.pathname, {
      hasCartItems: Array.isArray(cartItems) && cartItems.length > 0,
    }, t),
  );

  const chatboxRef = useRef(null);
  // Keyed by conversationHistory index — lets the scroll-to-new-question effect below
  // find the DOM node for the turn that was just added, without re-querying the DOM.
  const turnRefs = useRef({});
  // Captures the turn count as of mount so the effect below can tell "a new question was
  // just asked" (count increased) apart from "the current turn's answer is still
  // streaming in" (count unchanged) — only the former should move the scroll position.
  const prevTurnCountRef = useRef(conversationHistory.length);
  const activeStreamRef = useRef(null);
  const stageIntervalRef = useRef(null);
  // Cross-turn answer bleed guard: monotonic turn counter. updateLastAssistantMessage checks a
  // write's turnId against this before applying it, so a slow response from an OLDER turn (e.g.
  // a manual/RAG answer, which is materially slower than a fast structured tool call) can never
  // land on a NEWER turn's placeholder if it resolves after that newer turn has already started.
  // The normal path is already sequential (isAnalyzing gates handleSend), so this is
  // defense-in-depth for the case that invariant ever slips, not a fix for a confirmed-reproduced
  // race — see the diagnostic write-up in the bug-fixing docs.
  const turnIdRef = useRef(0);
  // Analytics only: which UI path (typed / suggestion / action_chip) queued the message
  // currently flowing through setSendMessage → handleSend. Set by each sender, consumed
  // and reset once inside handleSend.
  const sendSourceRef = useRef(null);
  // Keyed by conversationHistory index. Holds everything commitFeedback/handleUndoFeedback
  // need so neither has to read back from conversationHistory (which would be a stale
  // closure inside the setTimeout callback captured at click time).
  const pendingFeedbackTimers = useRef({});
  const feedbackCommits = useRef({});
  const feedbackConversation = useRef(conversationId);
  feedbackConversation.current = conversationId;

  // Single choke point for changing conversationId so every caller (initial generation,
  // the backend echoing back its own session_id, "clear chat") keeps sessionStorage in
  // sync — scattering sessionStorage.setItem at each call site was tried and is easy to
  // miss one of.
  const persistConversationId = (id) => {
    setConversationId(id);
    const uid = user?.user_id ?? null;
    if (uid === null) return;
    try {
      localStorage.setItem(chatKey(uid, "id"), id);
    } catch (_err) {
      // localStorage unavailable (e.g. private browsing) — conversation just won't persist.
    }
  };

  useEffect(() => {
    const uid = user?.user_id ?? null;
    // Only mint a fresh session id once we're logged in AND this user's stored chat has been
    // loaded — minting before the load would overwrite the restored session id.
    if (!conversationId && uid !== null && chatLoadedForUserRef.current === uid) {
      persistConversationId(`chip_${Date.now()}`);
    }
  }, [conversationId, user?.user_id]);

  // Load the authenticated user's persisted chat when their id becomes known or changes
  // (Copilot-style persistence). Per-user localStorage keys mean a login only ever reads its
  // OWN transcript — the cross-user isolation the old Bug #27 wipe protected is preserved by
  // the keying, so we no longer wipe on logout; the transcript survives for the next same-user
  // login. On logout (id -> null) we clear only the ON-SCREEN chat, never the stored keys.
  const prevUserIdRef = useRef(user?.user_id ?? null);
  useEffect(() => {
    const currentUserId = user?.user_id ?? null;
    if (prevUserIdRef.current === currentUserId && chatLoadedForUserRef.current === currentUserId) return;
    const switchingUsers = prevUserIdRef.current !== null && prevUserIdRef.current !== currentUserId;
    prevUserIdRef.current = currentUserId;
    // Abort any in-flight stream when the active user actually changes (logout / switch).
    if (switchingUsers && activeStreamRef.current) {
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }
    if (currentUserId === null) {
      // Logout: clear the visible chat but KEEP this user's stored transcript for next login.
      setConversationHistory([]);
      setConversationId("");
      setShowSuggestions(true);
      chatLoadedForUserRef.current = null;
      return;
    }
    // Login / user switch / hydration: restore THIS user's own stored transcript.
    let restored = [];
    let restoredId = "";
    try {
      const rawH = localStorage.getItem(chatKey(currentUserId, "history"));
      const parsed = rawH ? JSON.parse(rawH) : [];
      restored = Array.isArray(parsed) ? parsed : [];
      restoredId = localStorage.getItem(chatKey(currentUserId, "id")) || "";
    } catch (_err) {
      restored = [];
      restoredId = "";
    }
    setConversationHistory(restored);
    setConversationId(restoredId);
    if (restored.length > 0) setShowSuggestions(false);
    chatLoadedForUserRef.current = currentUserId; // unblocks the persist + mint effects
  }, [user?.user_id]);

  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      try {
        const status = await getChatbotStatus();
        if (mounted) setChatEnabled(Boolean(status?.data?.enabled ?? true));
      } catch (_error) {
        if (mounted) setChatEnabled(true);
      }
    };
    checkStatus();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setInitialQuestions(
      getInitialQuestionsForPath(location?.pathname || window.location.pathname, {
        hasCartItems: Array.isArray(cartItems) && cartItems.length > 0,
      }, t),
    );
    // i18n.language is included so switching the portal's language (Language.js, header
    // dropdown) re-translates the currently-shown suggested questions immediately, without
    // requiring a route change or cart update to happen to trigger a recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, cartItems, i18n.language]);

  useEffect(() => {
    if (sendMessage) {
      handleSend(input);
      setSendMessage(false);
    }
  }, [sendMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (activeStreamRef.current) activeStreamRef.current.abort();
    if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
    // Panel closed mid-retract-window: commit any still-pending feedback right away rather
    // than silently dropping it (an explicit Undo click is the only way to actually cancel it).
    Object.values(pendingFeedbackTimers.current).forEach((pending) => {
      clearTimeout(pending.timeoutId);
      submitChatbotFeedback(feedbackPayload(pending)).catch(() => {});
    });
    pendingFeedbackTimers.current = {};
  }, []);

  // On mount/remount (e.g. reopening the docked panel with existing history restored
  // from sessionStorage), jump straight to the bottom so the user sees where the
  // conversation left off — restores the panel's previous "always show the latest" behavior.
  useEffect(() => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chatboxRef.current) return;
    const prevCount = prevTurnCountRef.current;
    const currentCount = conversationHistory.length;

    if (currentCount > prevCount) {
      // A new question was just asked. Previously this effect always did
      // `scrollTop = scrollHeight`, which re-ran on every streamed chunk too — so once an
      // answer was more than one screen tall, the user was left staring at its tail and
      // had to scroll back up to read it from the start. Instead, jump to the TOP of the
      // newly-added turn (the user's question bubble) so the response is read top-down.
      const newTurnNode = turnRefs.current[currentCount - 1];
      if (newTurnNode) {
        // Align the new turn's top with the chatbox's top, scrolling ONLY the chatbox.
        // scrollIntoView({block:"start"}) was used before but (a) can scroll the wrong
        // scrollable ancestor and (b) its smooth animation gets cancelled by the streaming
        // re-renders that start a frame later, leaving the view stranded at the bottom so the
        // user had to scroll up to find the message. A direct container-scoped scrollTop is
        // deterministic and unaffected by the streaming updates.
        const box = chatboxRef.current;
        const delta = newTurnNode.getBoundingClientRect().top - box.getBoundingClientRect().top;
        box.scrollTop += delta;
      } else {
        chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
      }
    } else if (currentCount === 0) {
      chatboxRef.current.scrollTop = 0;
    }
    // currentCount === prevCount means the current turn's answer is still streaming in
    // (updateLastAssistantMessage mutates the last item without changing array length) —
    // deliberately leave the scroll position alone so the user keeps reading from the top.

    prevTurnCountRef.current = currentCount;
  }, [conversationHistory]);

  const startStageRotation = () => {
    setStageIndex(0);
    if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
    stageIntervalRef.current = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % STAGE_MESSAGES.length);
    }, 1300);
  };

  const stopStageRotation = () => {
    if (stageIntervalRef.current) {
      clearInterval(stageIntervalRef.current);
      stageIntervalRef.current = null;
    }
  };

  // `turnId`, when passed, must match BOTH the array's own last message (proves this write is
  // still landing on the placeholder it was meant for) AND the latest turn actually started
  // (proves a newer turn hasn't since begun) — either mismatching means this write is stale and
  // must be dropped, not silently applied to whatever now happens to be "last". Callers outside
  // a single chat turn's lifecycle (none currently exist in this file) can omit `turnId` to keep
  // the unguarded behavior.
  const updateLastAssistantMessage = (updater, turnId) => {
    setConversationHistory((prev) => {
      if (!prev.length) return prev;
      const idx = prev.length - 1;
      if (turnId !== undefined && (prev[idx]?.turnId !== turnId || turnId !== turnIdRef.current)) {
        // eslint-disable-next-line no-console
        console.warn(`CHIP: dropped a stale response update for turn ${turnId} (current turn is ${turnIdRef.current})`);
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...updater(next[idx]) };
      return next;
    });
  };

  const buildPayload = (message, sid) => ({
    question: message,
    session_id: sid,
    screen_context: {
      path: window.location.pathname,
      site_number: selectedSite || localStorage.getItem("selectedSite") || undefined,
      site_id: Number(selectedSiteData?.site_id) || undefined,
      site_name: selectedSiteData?.customer_site_name || undefined,
      // Portal's current i18next locale (e.g. "en", "fr", "es", "de" — see src/i18n.js),
      // so the backend's system prompt (system_prompt.js) can instruct the LLM to answer
      // in the SAME language the portal UI is currently set to, instead of always English.
      // Read straight from the live i18next instance (not React state) so it's always the
      // language active at send time, matching what CHIP's own static UI strings use below.
      language: String(i18n?.language || "en").toLowerCase().slice(0, 2),
    },
  });

  const mapAddress = (addr = {}, fallbackName = "") => ({
    name: String(addr?.address_name || fallbackName || "").trim(),
    street: [addr?.street1, addr?.street2, addr?.street3].filter(Boolean).join(", ").trim(),
    city: String(addr?.city || "").trim(),
    state: String(addr?.state || "").trim(),
    zip: String(addr?.zip || "").trim(),
    country: String(addr?.country || "").trim(),
  });

  const isAddressComplete = (addr = {}) =>
    ["name", "street", "city", "state", "zip", "country"].every((key) => String(addr?.[key] || "").trim());

  const fetchDefaultAddress = async (type) => {
    const sid = Number(selectedSiteData?.site_id || 0);
    if (!sid) return null;
    try {
      const resp = await getSiteAddressesNew(sid, type);
      const listRaw = resp?.data?.data ?? resp?.data ?? [];
      const list = Array.isArray(listRaw) ? [...listRaw] : [];
      list.sort((a, b) => (b?.is_default ? 1 : 0) - (a?.is_default ? 1 : 0));
      return list[0] || null;
    } catch (_err) {
      return null;
    }
  };

  const askCheckoutDetails = async (defaults = {}, allowEdit = false) => {
    const shipping = { ...(defaults?.shipping_address || {}) };
    const billing = { ...(defaults?.billing_address || {}) };
    const additional = { ...(defaults?.additional_details || {}) };
    const payment = { ...(defaults?.payment_details || {}) };

    const title = allowEdit ? "Update checkout details" : "Confirm checkout details";
    const result = await Swal.fire({
      title,
      width: 760,
      html: `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;text-align:left;">
          <input id="ship_name" class="swal2-input" placeholder="Shipping name" value="${shipping.name || ""}">
          <input id="bill_name" class="swal2-input" placeholder="Billing name" value="${billing.name || ""}">
          <input id="ship_street" class="swal2-input" placeholder="Shipping street" value="${shipping.street || ""}">
          <input id="bill_street" class="swal2-input" placeholder="Billing street" value="${billing.street || ""}">
          <input id="ship_city" class="swal2-input" placeholder="Shipping city" value="${shipping.city || ""}">
          <input id="bill_city" class="swal2-input" placeholder="Billing city" value="${billing.city || ""}">
          <input id="ship_state" class="swal2-input" placeholder="Shipping state" value="${shipping.state || ""}">
          <input id="bill_state" class="swal2-input" placeholder="Billing state" value="${billing.state || ""}">
          <input id="ship_zip" class="swal2-input" placeholder="Shipping zip" value="${shipping.zip || ""}">
          <input id="bill_zip" class="swal2-input" placeholder="Billing zip" value="${billing.zip || ""}">
          <input id="ship_country" class="swal2-input" placeholder="Shipping country" value="${shipping.country || ""}">
          <input id="bill_country" class="swal2-input" placeholder="Billing country" value="${billing.country || ""}">
          <input id="delivery_terms" class="swal2-input" placeholder="Delivery terms (e.g. DAP)" value="${additional.delivery_terms || ""}">
          <input id="mode_delivery" class="swal2-input" placeholder="Mode of delivery" value="${additional.mode_of_delivery || ""}">
          <select id="ship_complete" class="swal2-select" style="grid-column:span 1;">
            <option value="No" ${(additional.ship_complete || "No") === "No" ? "selected" : ""}>Ship complete: No</option>
            <option value="Yes" ${(additional.ship_complete || "No") === "Yes" ? "selected" : ""}>Ship complete: Yes</option>
          </select>
          <select id="payment_mode" class="swal2-select" style="grid-column:span 1;">
            <option value="PO" ${(payment.payment_mode || "PO") === "PO" ? "selected" : ""}>Payment mode: PO</option>
            <option value="CC" ${(payment.payment_mode || "PO") === "CC" ? "selected" : ""}>Payment mode: CC</option>
          </select>
          <input id="po_number" class="swal2-input" style="grid-column:span 2;" placeholder="PO Number (required for PO mode)" value="${payment.payment_details?.po_number || ""}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Place Order",
      preConfirm: () => {
        const out = {
          shipping_address: {
            name: document.getElementById("ship_name").value.trim(),
            street: document.getElementById("ship_street").value.trim(),
            city: document.getElementById("ship_city").value.trim(),
            state: document.getElementById("ship_state").value.trim(),
            zip: document.getElementById("ship_zip").value.trim(),
            country: document.getElementById("ship_country").value.trim(),
          },
          billing_address: {
            name: document.getElementById("bill_name").value.trim(),
            street: document.getElementById("bill_street").value.trim(),
            city: document.getElementById("bill_city").value.trim(),
            state: document.getElementById("bill_state").value.trim(),
            zip: document.getElementById("bill_zip").value.trim(),
            country: document.getElementById("bill_country").value.trim(),
          },
          additional_details: {
            delivery_terms: document.getElementById("delivery_terms").value.trim(),
            ship_complete: document.getElementById("ship_complete").value,
            mode_of_delivery: document.getElementById("mode_delivery").value.trim(),
            freight_number: "",
            order_notes: "",
          },
          payment_details: {
            payment_mode: document.getElementById("payment_mode").value,
            payment_details: {
              po_number: document.getElementById("po_number").value.trim(),
              po_file: "",
              po_filename: "",
            },
          },
        };

        const shippingOk = isAddressComplete(out.shipping_address);
        const billingOk = isAddressComplete(out.billing_address);
        if (!shippingOk || !billingOk) {
          Swal.showValidationMessage("Shipping and billing address fields are required.");
          return null;
        }
        if (!out.additional_details.delivery_terms || !out.additional_details.mode_of_delivery) {
          Swal.showValidationMessage("Delivery terms and mode of delivery are required.");
          return null;
        }
        if (out.payment_details.payment_mode === "PO" && !out.payment_details.payment_details.po_number) {
          Swal.showValidationMessage("PO number is required for PO payment mode.");
          return null;
        }
        return out;
      },
    });

    return result?.isConfirmed ? result.value : null;
  };

  const runPlaceOrderWizard = async (action = {}) => {
    const siteNumber = String(action?.site_number || selectedSite || "").trim();
    const siteId = Number(selectedSiteData?.site_id || 0);
    if (!siteNumber || !siteId) {
      await Swal.fire("Missing Site", "Select a valid site before placing an order.", "warning");
      return;
    }

    const cartForSite = (Array.isArray(cartItems) ? cartItems : []).filter((item) => String(item?.Site || "").trim() === siteNumber);
    if (!cartForSite.length) {
      await Swal.fire("Empty Cart", `Your cart is empty for site ${siteNumber}.`, "info");
      return;
    }

    const shippingDefaultRaw = await fetchDefaultAddress("Shipping");
    const billingDefaultRaw = await fetchDefaultAddress("Billing");
    const shippingDefault = mapAddress(shippingDefaultRaw, selectedSiteData?.address_name_ln || "Shipping");
    const billingDefault = mapAddress(billingDefaultRaw, "Billing");
    const baseDefaults = {
      shipping_address: shippingDefault,
      billing_address: billingDefault,
      additional_details: {
        delivery_terms: "DAP",
        ship_complete: "No",
        mode_of_delivery: "Standard",
        freight_number: "",
        order_notes: "",
      },
      payment_details: {
        payment_mode: "PO",
        payment_details: {
          po_number: "",
          po_file: "",
          po_filename: "",
        },
      },
    };

    const choice = await Swal.fire({
      title: "Place Order",
      text: "Use default checkout values or change required fields?",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Use Defaults",
      denyButtonText: "Change Values",
    });
    if (choice.isDismissed) return;

    const checkoutInput = await askCheckoutDetails(baseDefaults, choice.isDenied);
    if (!checkoutInput) return;

    const productIds = cartForSite.map((item) => Number(item?.ProductID || item?.product_id)).filter((id) => Number.isFinite(id) && id > 0);
    const productResp = await getProductsById(productIds, siteNumber);
    const fullProducts = Array.isArray(productResp?.data) ? productResp.data : [];
    const productMap = new Map(fullProducts.map((p) => [Number(p.product_id), p]));
    const currency = String(selectedSiteData?.site_currency || "USD").toLowerCase();

    const cartProducts = cartForSite
      .map((item) => {
        const pid = Number(item?.ProductID || item?.product_id);
        const p = productMap.get(pid);
        if (!p) return null;
        const qty = Math.max(1, Number(item?.Quantity || 1));
        const price = Number(p?.[`price_${currency}`] || 0);
        return {
          product_id: p.product_id,
          product_name: p.product_name,
          product_number: p.product_number,
          ProductQty: qty,
          ProductPrice: price,
          total: (qty * price).toFixed(2),
        };
      })
      .filter(Boolean);

    if (!cartProducts.length) {
      await Swal.fire("No Products", "Could not resolve cart products for order placement.", "error");
      return;
    }

    const productTotal = cartProducts.reduce((sum, p) => sum + Number(p.total || 0), 0);
    const taxes = (productTotal * 0.08).toFixed(2);
    const discounts = (productTotal * 0.05).toFixed(2);
    const shipping = 50.0;
    const total = (productTotal + Number(taxes) - Number(discounts) + shipping).toFixed(2);

    const payload = {
      ...checkoutInput,
      order_details: {
        products: cartProducts,
        taxes,
        shipping,
        discounts,
        total,
        currency,
      },
      selectedSite: siteId,
      loggedin_user: user?.user_id,
      loggedin_user_email: user?.user_email || "",
      loggedin_user_name: `${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim(),
      loggedin_user_phone: user?.user_phone || "",
    };

    const response = await createOrder(payload);
    if (response?.status && response?.data) {
      trackChipUx("chip_wizard_order_placed", { order_number: response?.data?.order_number || null }, conversationId);
      await clearCart();
      await refreshCart();
      await Swal.fire("Order Placed", `Order created successfully: ${response?.data?.order_number || ""}`, "success");
      setConversationHistory((prev) => [
        ...prev,
        {
          user: "Place order",
          response: `Done. Order placed successfully${response?.data?.order_number ? `: ${response.data.order_number}` : ""}.`,
          actions: [],
          pendingAction: null,
          data: null,
          toolsUsed: ["prepare_place_order"],
          timestamp: new Date().toISOString(),
        },
      ]);
      return;
    }

    trackChipUx("chip_wizard_order_failed", {}, conversationId);
    await Swal.fire("Order Failed", response?.data?.message || "Failed to place order.", "error");
  };

  const hydrateProducts = async (rawData) => {
    if (!rawData || !selectedSite) return rawData;
    const rows = [
      ...(Array.isArray(rawData?.products) ? rawData.products : []),
      ...(Array.isArray(rawData?.rows) ? rawData.rows : []),
      ...(Array.isArray(rawData?.items) ? rawData.items : []),
    ];

    const productIdSet = new Set();
    rows.forEach((row) => {
      if (row?.productData?.product_id) productIdSet.add(Number(row.productData.product_id));
      else if (row?.product_id) productIdSet.add(Number(row.product_id));
      else if (row?.ProductID) productIdSet.add(Number(row.ProductID));
    });

    const productIds = Array.from(productIdSet).filter((id) => Number.isFinite(id) && id > 0);
    if (!productIds.length) return rawData;

    try {
      const resp = await getProductsById(productIds, selectedSite);
      const full = Array.isArray(resp?.data) ? resp.data : [];
      const productMap = new Map(full.map((p) => [Number(p.product_id), p]));
      // eslint-disable-next-line no-console
      console.log(
        "[CHIP hydrateProducts] sent ids:", productIds,
        "| data.rows:", (rawData?.rows || []).map((r) => r.product_number || r.sub_name || r.name),
        "| data.products:", (rawData?.products || []).map((r) => r.product_number || r.sub_name),
        "| byids returned:", full.map((p) => p.product_number),
      );
      return { ...rawData, _productMap: productMap };
    } catch (_error) {
      return rawData;
    }
  };

  const applyFinalAssistantResponse = async (payload, fallbackText, turnId) => {
    const assistantText =
      payload?.response ||
      payload?.data?.answer ||
      payload?.data?.response ||
      fallbackText ||
      t("UI_TEXT.SORRY_THERE_WAS_AN_ERROR_PROCESSING_YOUR_REQUEST");
    const assistantActions = normalizeAssistantActions(payload?.actions);
    const pendingAction = payload?.pending_action || null;
    const rawData = payload?.data || null;

    // Apply text + actions immediately so the user sees the final response without
    // waiting for the product-hydration API call.
    updateLastAssistantMessage(() => ({
      response: assistantText,
      actions: assistantActions,
      pendingAction,
      data: rawData,
      toolsUsed: payload?.tools_used || [],
      auditId: payload?.audit_id || null,
      feedback: null,
      timestamp: new Date().toISOString(),
    }), turnId);

    // Hydrate products in the background; patch only the data field once ready.
    hydrateProducts(rawData).then((hydratedData) => {
      if (hydratedData !== rawData) {
        updateLastAssistantMessage((prev) => ({ ...prev, data: hydratedData }), turnId);
      }
    }).catch(() => {});
    if (payload?.session_id) persistConversationId(payload.session_id);

      // payload.agent_actions (top-level) and payload.data.agent_actions (nested — `data` is
      // the tool's own raw return value, which already carries its own agent_actions field as
      // part of the established tool-result shape) legitimately both end up populated with the
      // SAME array for any tool that sets agent_actions directly on its result — this was
      // always silently duplicating every action, but went unnoticed for navigate/switch_site
      // (harmless to run twice) until workorder_report_not_found's Swal.fire (which blocks on
      // user interaction) made it obvious: the popup reappeared immediately after being
      // dismissed, because the "duplicate" action was still queued right behind it. Dedupe by
      // content before processing so every action type only actually runs once per turn.
      const rawAgentActions = [
        ...(Array.isArray(payload?.agent_actions) ? payload.agent_actions : []),
        ...(Array.isArray(payload?.data?.agent_actions) ? payload.data.agent_actions : []),
      ];
      const seenActionKeys = new Set();
      const agentActions = rawAgentActions.filter((action) => {
        const key = JSON.stringify(action);
        if (seenActionKeys.has(key)) return false;
        seenActionKeys.add(key);
        return true;
      });
      // Shared by the "navigate" branch's absolute-URL case below AND the dedicated
      // "open_external" type — split out so a tool can emit BOTH "open this report PDF in a
      // new tab" AND "navigate the app itself to a real route" in the same turn (e.g.
      // get_workorder_report_link's report + WORKORDERS_ROUTE?open_wo=... deep link) without
      // the two colliding — mergeAgentActions/_agentActions.js dedupes agent_actions by
      // `type`, so two entries both typed "navigate" would collapse to just the last one.
      const openExternalUrl = async (dest) => {
        // Live-reported 2026-07-22: after adding the pre-warm retry below (for the earlier
        // intermittent-400 bug), the report tab stopped opening at all — no error, NO network
        // activity whatsoever, not even the HEAD pre-check. That's the signature of the
        // browser's OWN popup blocker silently killing window.open(): most browsers only allow
        // an unrequested window.open() within a short window after the triggering user
        // gesture ("transient activation"), and the retry's up to ~2.1s of awaited fetch/delay
        // was enough to run out that clock (on top of however long the chat turn itself
        // already took) — the call never even reaches the network because the browser refuses
        // to open the tab at all. Fix: open the tab IMMEDIATELY, synchronously, blank, as the
        // very first thing this function does (before any await) — that's within the user
        // gesture's activation window and reliably allowed — then just redirect that ALREADY
        // OPEN window once the retry logic below finishes. Navigating a window you already
        // hold a reference to is a normal same-window navigation, not a new popup, so it is
        // never subject to popup-blocker rules regardless of how long the retry takes.
        // User-reported 2026-07-22: "take me to training" opened a BLANK tab, while the login
        // link embedded in the chat answer's own text was correct. Root cause: window.open()
        // returns null WHEN "noopener" is passed — that's the browser's actual, documented
        // behavior (the whole point of noopener is to withhold the reference), which defeats
        // the entire point of pre-opening a blank tab to redirect later. `newTab` was always
        // null below, so `if (newTab && !newTab.closed)` always failed, falling to the "else"
        // branch — which opened a SECOND, separate window.open(dest, "_blank", "noopener") tab
        // with the real URL, leaving the FIRST (blank) tab permanently blank and unredirected,
        // and risking the second one being blocked outright as an extra unrequested popup.
        // Fix: omit "noopener" on THIS call so we actually get the window reference back, then
        // sever window.opener manually right before redirecting — same reverse-tabnabbing
        // protection as the "noopener" feature, without losing the reference it would have
        // withheld.
        const newTab = window.open("", "_blank");
        // Live-reported 2026-07-22: opening a workorder report PDF (an /assets/ link on our
        // OWN backend) intermittently 400'd on the very first open, working every time on a
        // manual reload of the same tab — machine-dependent (some laptops never see it),
        // nothing logged server-side for the failing request, and the report-generation code
        // itself already guarantees the file is fully written before this URL is ever handed
        // out (see services.controller.js). That combination points to something on the
        // CLIENT machine (security/AV web-shield, corporate proxy) intercepting a brand-new
        // URL once, not a bug in our own request/response. We have no hook to retry INSIDE
        // the opened tab (it's a bare file, none of our JS runs there), so pre-warm the exact
        // same URL from here first — where we do control retries — and only redirect the
        // already-open tab once it succeeds (or after we've genuinely exhausted attempts).
        // Scoped to our own /assets/ paths only: a real third-party destination (e.g. the AMF
        // Premium portal link) may not support HEAD or permissive CORS, and opening it should
        // stay instant, not pay this retry cost for no reason.
        let destPath = "";
        try { destPath = new URL(dest).pathname; } catch (_) { /* leave blank */ }
        if (destPath.startsWith("/assets/")) {
          for (let attempt = 0; attempt < 3; attempt++) {
            let ok = false;
            try {
              // eslint-disable-next-line no-await-in-loop
              const res = await fetch(dest, { method: "HEAD", cache: "no-store" });
              ok = res.ok;
            } catch (_) { /* network/CORS hiccup — treat as not-ready, retry */ }
            if (ok) break;
            if (attempt < 2) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, 700));
            }
          }
        }
        // Sever window.opener manually — same reverse-tabnabbing protection "noopener" gives,
        // applied AFTER already holding the reference instead of via the feature flag (which
        // would have withheld it, per the note above). Deliberately no "noreferrer" — see the
        // git history on this line for why (stripped Referer broke the same /assets/ report
        // link this function now pre-warms above).
        if (newTab && !newTab.closed) {
          try { newTab.opener = null; } catch (_) { /* some browsers disallow the assignment; harmless */ }
          newTab.location.href = dest;
        } else {
          // The blank open itself was blocked (activation window already gone before this
          // function even ran) — one last direct attempt; no worse than the pre-fix behavior.
          window.open(dest, "_blank", "noopener");
        }
      };
      for (const action of agentActions) {
      const actionType = String(action?.type || "").toLowerCase();
      trackChipUx("chip_agent_action", {
        action_type: actionType,
        // which page/site the chat opened — feeds the Pages Opened drill-down
        path: action?.path ? String(action.path).slice(0, 200) : undefined,
        site_number: action?.site_number ? String(action.site_number).slice(0, 64) : undefined,
      }, payload?.session_id || conversationId);
      if (actionType === "refresh_cart") {
        refreshCart();
      } else if (actionType === "switch_site") {
        const nextSite = String(action?.site_number || "").trim();
        if (nextSite) {
          updateSite(nextSite, Array.isArray(siteData) ? siteData : []);
        }
      } else if (actionType === "open_external") {
        const dest = String(action?.path || "").trim();
        if (dest) await openExternalUrl(dest);
      } else if (actionType === "navigate") {
        let dest = String(action?.path || "").trim();
        // NOTE: do NOT strip "open_create_case=1" here. It is send_support_request's deliberate
        // deep-link that tells Support.js to auto-open the Create Case modal — the whole point of
        // "create a case" from chat. A bare "go to support" routes through get_navigation_help and
        // never carries this flag, so honoring it here does not force the modal open uninvited.
        // An absolute external URL (e.g. the AMF Premium login portal) can't be reached via
        // React Router's navigate() at all — it only resolves internal app routes. Previously
        // this silently did nothing useful for an external destination (live-reproduced: the
        // premium-subscribed "take me to premium" flow never actually opened the login page).
        // Open external destinations in a new tab instead so the app/chat state isn't lost.
        // Kept here (in addition to the dedicated "open_external" type above) for any tool
        // still sending an absolute URL as a bare "navigate" — e.g. the AMF Premium link.
        if (/^https?:\/\//i.test(dest)) {
          await openExternalUrl(dest);
        } else if (dest) {
          // Model-viewer/2D-viewer routes are standalone (outside DefaultLayout — see
          // App.js:80-81), so navigating there unmounts ChatBot entirely. navigate() was
          // previously called synchronously right after updateLastAssistantMessage() above,
          // which only schedules a state update — React had not necessarily committed it (and
          // the sessionStorage-persist effect had not necessarily run) before the route swap
          // unmounted this component. Live-reproduced: "show me model for machine X" opened
          // the viewer correctly, but closing it and returning showed the chat still stuck on
          // "Looking into it…" instead of the real "Opening the model…" answer, because the
          // stale pre-answer state was what got persisted (or nothing was persisted at all).
          // Deferring navigate() to a macrotask lets the pending state update and its effect
          // flush first, so the answer text is safely committed to sessionStorage before
          // anything unmounts.
          setTimeout(() => {
            navigate(dest);
            // Live-reported 2026-07-21 (#22): navigating to a data page (/machines etc.) showed
            // no data because the page self-fetches on mount and races its not-yet-settled site
            // context. The retry net is the chip:navigate event below — but that fires
            // SYNCHRONOUSLY before this navigate, so on a FRESH navigation the destination page
            // has not mounted/subscribed yet and misses it (the retry only ever helped when
            // already on the page). Re-dispatch on the tick AFTER the route change so a
            // freshly-mounted page's listener receives it too.
            setTimeout(() => window.dispatchEvent(new CustomEvent("chip:navigate", { detail: { path: dest } })), 0);
          }, 0);
          // React Router's location doesn't reliably retrigger a page's own location-based
          // effects when the destination happens to be textually IDENTICAL to the current
          // location (same "same-route no-op" class already hit by refresh_request_list
          // above) — live-reproduced on Cases.js: "show me closed cases" then "show me all
          // cases" left the Closed filter stuck, since the second navigate's bare destination
          // matched what the first request's own strip-navigate had already left the URL at.
          // Dispatch the raw destination as an event too so pages that need every chat-driven
          // navigate — not just ones that change location.search — can react directly.
          window.dispatchEvent(new CustomEvent("chip:navigate", { detail: { path: dest } }));
        }
      } else if (actionType === "refresh_request_list") {
        // No shared context exists for the request list (unlike cart, which has
        // useCart()/refreshCart()), so navigate() alone was a same-route no-op whenever
        // the user was already on /requests (very common — chat is a docked side panel) —
        // nothing ever visibly updated without a manual browser refresh. Requests.js now
        // also listens for this window event and refetches directly, independent of
        // whether a route change actually occurs.
        window.dispatchEvent(new Event("requestList:refresh"));
        navigate(ROUTES.REQUESTS);
      } else if (actionType === "refresh_saved_for_later") {
        // Unlike refresh_request_list/refresh_recommendations below, SavedForLater.js has no
        // dedicated window-event listener of its own — it only bumps its refetch-triggering
        // refreshTick off "chip:navigate" (mirroring MyMachines.js's pattern). A bare navigate()
        // to the same route is a no-op when already on /saved_for_later, so that event must be
        // dispatched here too or a chat-driven add/remove never refetches without a manual reload.
        navigate(ROUTES.SAVED_FOR_LATER);
        window.dispatchEvent(new CustomEvent("chip:navigate", { detail: { path: ROUTES.SAVED_FOR_LATER } }));
      } else if (actionType === "refresh_recommendations") {
        // Mirrors refresh_request_list above: navigate() alone is a same-route no-op when
        // the user is already on /recommendations (common — chat is a docked side panel),
        // so a list CHIP just created/updated never appears without a manual browser
        // refresh. Recommendations.js also listens for this window event and refetches
        // directly, independent of whether a route change actually occurs.
        window.dispatchEvent(new Event("recommendations:refresh"));
        navigate(ROUTES.RECOMMENDATIONS);
      } else if (actionType === "open_place_order_form") {
        try {
          // eslint-disable-next-line no-await-in-loop
          await runPlaceOrderWizard(action);
        } catch (_err) {
          await Swal.fire("Order Flow Error", "Could not open place-order form.", "error");
        }
      } else if (actionType === "workorder_report_not_found") {
        // Mirrors WorkOrders.js's own handleViewReport exactly (same icon/title/text/button)
        // so a chat-triggered "no report" request looks identical to clicking the report icon
        // directly on the page, instead of only getting a bare chat sentence.
        // eslint-disable-next-line no-await-in-loop
        await Swal.fire({
          icon: "warning",
          title: "Report Not Found",
          text: "The requested work order report could not be found.",
          confirmButtonText: "OK",
        });
      } else if (actionType) {
        window.dispatchEvent(new CustomEvent("chip:agent_action", { detail: action }));
      }
    }
  };

  const handleSend = async (inputQuestion) => {
    const question = String(inputQuestion || "").trim();
    if (!question || isAnalyzing) return;
    const displayText = pendingDisplayText;
    setPendingDisplayText(null);

    const sid = conversationId || `chip_${Date.now()}`;
    // Analytics: which UI path produced this message. The ref is set by the three
    // senders (typed / suggestion / action_chip) right before setSendMessage(true)
    // and consumed exactly once here, so a later programmatic send can't inherit a
    // stale source.
    const inputSource = sendSourceRef.current || "typed";
    sendSourceRef.current = null;
    trackChipUx("chip_message_sent", { message_length: question.length, input_source: inputSource }, sid);
    // This turn's id — every updateLastAssistantMessage call below is tagged with it so a
    // response that resolves after a NEWER turn has already started gets dropped instead of
    // overwriting that newer turn's placeholder. See turnIdRef's declaration for the full
    // rationale (cross-turn answer bleed guard).
    const myTurnId = ++turnIdRef.current;
    setInput("");
    setShowSuggestions(false);
    setIsAnalyzing(true);
    startStageRotation();

    setConversationHistory((prev) => [
      ...prev,
      {
        user: displayText || question,
        screenContext: buildPayload(question, sid).screen_context,
        response: null,
        actions: [],
        pendingAction: null,
        data: null,
        toolsUsed: [],
        timestamp: new Date().toISOString(),
        turnId: myTurnId,
      },
    ]);

    try {
      await flushFeedbackBeforeRetry(pendingFeedbackTimers.current, feedbackCommits.current, commitFeedback);
      const payload = buildPayload(question, sid);
      let streamedText = "";
      let receivedDone = false;

      try {
        const controller = new AbortController();
        activeStreamRef.current = controller;
        await streamChatbot(payload, {
          signal: controller.signal,
          onEvent: async (event) => {
            if (!event) return;
            if (event.type === "chunk") {
              streamedText += String(event.chunk || "");
              updateLastAssistantMessage(() => ({ response: streamedText }), myTurnId);
            }
            if (event.type === "done") {
              receivedDone = true;
              await applyFinalAssistantResponse(event, streamedText, myTurnId);
            }
            if (event.type === "error") {
              throw new Error(event.message || "CHIP streaming failed");
            }
          },
        });
        // The stream ended WITHOUT a `done` event — the connection dropped after some chunks
        // but before the authoritative final payload (live-reported "got stuck": the bubble was
        // left showing a half-sentence like "I'm sorry, but I couldn't open the" forever). Fall
        // back to the non-streaming endpoint to reconcile to the complete answer; the server dedup
        // (runOnce, keyed on request_id) returns the already-computed result without re-running tools.
        if (!receivedDone) {
          trackChipUx("chip_stream_fallback", { reason: "no_done" }, sid);
          const response = await askChatbot(payload);
          await applyFinalAssistantResponse(response, streamedText, myTurnId);
        }
      } catch (streamError) {
        // A deliberate Stop click aborts the fetch, which rejects with an AbortError —
        // that is NOT a failure to recover from. Previously this fell through to
        // askChatbot() unconditionally, silently starting a second, non-cancelable
        // request that reran the same turn (and could re-execute a write action the
        // first attempt had already triggered server-side).
        if (streamError?.name === "AbortError") {
          // Without this, the message bubble is left with response: null forever — it
          // was created with a null response when the turn started, and nothing else
          // ever fills it in for an aborted turn, so it renders the loading skeleton
          // indefinitely even though isAnalyzing has already reset and the composer is
          // usable again. Falls back to the stopped-response text only when nothing had
          // streamed in yet; otherwise keeps whatever partial text already arrived.
          updateLastAssistantMessage(() => ({
            response: streamedText || t("UI_TEXT.RESPONSE_STOPPED"),
            actions: [],
            pendingAction: null,
            timestamp: new Date().toISOString(),
          }), myTurnId);
          return;
        }
        trackChipUx("chip_stream_fallback", { reason: "stream_error" }, sid);
        const response = await askChatbot(payload);
        await applyFinalAssistantResponse(response, streamedText, myTurnId);
      } finally {
        activeStreamRef.current = null;
      }
    } catch (error) {
      // Surface the real failure when one exists (network/backend/stream error) instead of
      // only the generic apology, so the user gets an actionable hint — but stay graceful:
      // fall back to the generic text alone when there's no useful message on the error.
      const detail = error?.message ? ` (${error.message})` : "";
      trackChipUx("chip_client_error", {}, sid);
      updateLastAssistantMessage(() => ({
        response: `${t("UI_TEXT.SORRY_THERE_WAS_AN_ERROR_PROCESSING_YOUR_REQUEST")}${detail}`,
        actions: [],
        pendingAction: null,
        timestamp: new Date().toISOString(),
      }), myTurnId);
    } finally {
      stopStageRotation();
      setIsAnalyzing(false);
    }
  };

  const handleStopStreaming = () => {
    if (activeStreamRef.current) {
      trackChipUx("chip_stopped", {}, conversationId);
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }
    stopStageRotation();
    setIsAnalyzing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendSourceRef.current = "typed";
      setSendMessage(true);
    }
  };

  const handleSuggestedQuestion = (question) => {
    trackChipUx("chip_suggestion_clicked", { label: String(question || "").slice(0, 120) }, conversationId);
    sendSourceRef.current = "suggestion";
    setInput(question);
    setSendMessage(true);
  };

  const handleActionClick = (actionInput) => {
    const action = typeof actionInput === "string" ? { prompt: actionInput } : (actionInput || {});
    // Every chip — question or agentic — must always go through CHIP so the chat pane
    // always shows a response or an injected next prompt. Navigation is handled after
    // the reply arrives, via the backend's agent_actions, never as an early-return here.
    const prompt = action?.prompt || action?.label;
    if (!prompt) return;
    trackChipUx("chip_action_clicked", {
      kind: String(action?.action_type || "").toLowerCase() === "agentic" ? "agentic" : "question",
    }, conversationId);
    sendSourceRef.current = "action_chip";
    setPendingDisplayText(action?.label && action.label !== prompt ? action.label : null);
    setInput(prompt);
    setSendMessage(true);
  };

  // Clickable "ask:" links inside the bot's own markdown (see ChatMarkdown.jsx) — lets a
  // pick-list render as inline links in the message instead of duplicating every option as a
  // separate SHORTCUTS chip below it. Ref-held so the listener registers once but always calls
  // the current handler.
  const actionClickRef = useRef(handleActionClick);
  actionClickRef.current = handleActionClick;
  useEffect(() => {
    const onAsk = (e) => {
      const prompt = e?.detail?.prompt;
      if (prompt) actionClickRef.current({ prompt });
    };
    window.addEventListener("chip:ask", onAsk);
    return () => window.removeEventListener("chip:ask", onAsk);
  }, []);

  // "doc:<id>#page=N" source links (see ChatMarkdown.jsx) open in the SAME in-app viewer the
  // Documentation page uses (DocumentViewModal), rather than a new browser tab.
  //
  // The tab approach could not be made to work: the PDF lives in a private blob store, so a SAS URL
  // has to be fetched first, and that await spends the click's transient user activation -- Chrome
  // then blocks window.open and the user got "Allow pop-ups to open the document" (live-reported
  // 2026-07-26). Turning it into a plain <a href> instead fails for a different reason: the portal
  // authenticates with an Authorization: Bearer header, which an anchor cannot send, so the request
  // 401s before it reaches any route.
  //
  // The modal sidesteps both, because it is ordinary authenticated JS -- DocumentViewModal fetches
  // the SAS itself with the user's token, exactly as the Documentation page does. No pop-up to
  // block, no auth exemption to carve out, no expiring URL baked into the message, and the user
  // stays in the chat.
  useEffect(() => {
    const onOpenDoc = (e) => {
      const { documentId, page, name } = e?.detail || {};
      if (!documentId) return;
      setDocModal({ open: true, document: { id: documentId, name: name || "" }, page: page || null });
    };
    window.addEventListener("chip:opendoc", onOpenDoc);
    return () => window.removeEventListener("chip:opendoc", onOpenDoc);
  }, []);

  const handleClearResults = async () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }
    stopStageRotation();
    const currentSid = conversationId;
    trackChipUx("chip_cleared", {}, currentSid || null);
    setShowSuggestions(true);
    setConversationHistory([]);
    persistConversationId(`chip_${Date.now()}`);
    if (currentSid) {
      try {
        await clearChatbotSession({ session_id: currentSid });
      } catch (_error) {
        // ignore clear failures
      }
    }
  };

  // How long a thumbs up/down sits as "pending" before it's actually written — gives an
  // accidental click a real way back via the Undo affordance instead of an instant, permanent
  // /chip/feedback write.
  const FEEDBACK_RETRACT_WINDOW_MS = 8000;

  const commitFeedback = async (index) => {
    const pending = pendingFeedbackTimers.current[index];
    if (!pending) return;
    if (feedbackCommits.current[pending.auditId]) return feedbackCommits.current[pending.auditId];
    clearTimeout(pending.timeoutId);
    delete pendingFeedbackTimers.current[index];
    const savingFeedback = (async () => {
      try {
        const response = await submitChatbotFeedback(feedbackPayload(pending));
        if (!response?.status) throw new Error(response?.message || "Feedback could not be saved. Retry to try again.");
        // Emit analytics only after the feedback has been persisted successfully.
        trackChipUx("chip_feedback", {
          feedback: pending.type,
          has_correction: Boolean(pending.correction),
          audit_id: pending.auditId || null,
        }, pending.sessionId || conversationId);
        setConversationHistory((prev) => {
          const next = [...prev];
          if (next[index]?.auditId !== pending.auditId) return prev;
          next[index] = { ...next[index], feedback: pending.type, pendingFeedback: null, feedbackError: null };
          return next;
        });
      } catch (error) {
        if (feedbackConversation.current === pending.sessionId
          && (!pendingFeedbackTimers.current[index] || pendingFeedbackTimers.current[index].auditId === pending.auditId)) {
          pendingFeedbackTimers.current[index] = { ...pending, timeoutId: null };
        }
        setConversationHistory((prev) => prev.map((item, at) => at === index && item.auditId === pending.auditId ? { ...item, feedbackError: error.message } : item));
        throw error;
      }
    })();
    feedbackCommits.current[pending.auditId] = savingFeedback;
    try { await savingFeedback; }
    finally { delete feedbackCommits.current[pending.auditId]; }
  };

  const handleUndoFeedback = (index) => {
    const pending = pendingFeedbackTimers.current[index];
    if (!pending) return;
    clearTimeout(pending.timeoutId);
    delete pendingFeedbackTimers.current[index];
    setConversationHistory((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], pendingFeedback: null, feedbackCorrection: null, feedbackError: null };
      return next;
    });
  };

  const handleResponseFeedback = async (index, type) => {
    const item = conversationHistory?.[index];
    if (!item?.auditId || item?.feedback || item?.pendingFeedback) return;
    // Capture the reviewed conversation before the optional feedback dialog can
    // yield. Later turns or navigation must not replace the original evidence.
    const contextSnapshot = captureFeedbackContext(conversationHistory, index);

    let correction = "";
    if (type === "not_helpful") {
      const result = await Swal.fire({
        title: "Help Ask CHIP improve",
        text: "What would make this answer more useful?",
        input: "textarea",
        inputPlaceholder: "Type your feedback (optional)",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Skip",
        inputAttributes: {
          maxlength: 2000,
          "aria-label": "Feedback input",
        },
      });
      if (result.isDismissed) return;
      correction = String(result.value || "").trim();
    }
    if (feedbackConversation.current !== conversationId) return;

    setConversationHistory((prev) => {
      const next = [...prev];
      if (next[index]?.auditId !== item.auditId) return prev;
      next[index] = { ...next[index], pendingFeedback: type, feedbackCorrection: correction || null };
      return next;
    });

    pendingFeedbackTimers.current[index] = {
      type,
      auditId: item.auditId,
      correction: correction || null,
      sessionId: conversationId || null,
      userMessage: item.user || null,
      botResponse: item.response || null,
      screenContext: item.screenContext || null,
      contextSnapshot,
      timeoutId: setTimeout(() => { commitFeedback(index).catch(() => {}); }, FEEDBACK_RETRACT_WINDOW_MS),
    };
  };

  const renderDataBlocks = (item) => {
    const data = item?.data || {};
    const availableToPurchaseRowsRaw = Array.isArray(data?.available_to_purchase) ? data.available_to_purchase : [];
    const requestListRowsRaw = Array.isArray(data?.request_list) ? data.request_list : [];
    const availableToPurchase = availableToPurchaseRowsRaw.map((row) => normalizeRequestRow(row, "available")).filter(Boolean);
    const requestListRows = requestListRowsRaw.map((row) => normalizeRequestRow(row, "request")).filter(Boolean);
    const hasRequestBuckets = availableToPurchase.length > 0 || requestListRows.length > 0;
    const docs = [
      ...(Array.isArray(data?.documents) ? data.documents : []),
      ...(Array.isArray(data?.matches) ? data.matches : []),
    ].slice(0, 4);
    // Charts intentionally disabled in CHIP — never render analytics charts in chat (they were
    // low-value / often empty). The backend may still return data.charts; we just ignore it.
    const charts = [];

    // One compact table per turn replaces the old per-entity cards (work orders, orders,
    // machines, parts, cases, sites, plus any unrecognized array). resolveDataTable picks the
    // first recognized non-empty array. Skip a parts/products table when the interactive
    // request-list buckets below already render those same items with add-to-cart controls.
    // Single-record detail blocks (fuller than the compact 1-row table): order (track_order →
    // data.order.order), case (data.case), work order (data.workorder), part (data.product).
    // When one is present, suppress the redundant compact table for the same entity below.
    const orderDetail = data?.order?.order && typeof data.order.order === "object" ? data.order.order : null;
    const orderLineItems = orderDetail && Array.isArray(orderDetail.products) ? orderDetail.products : [];
    const caseDetail = data?.case && typeof data.case === "object" ? data.case : null;
    const woDetail = data?.workorder && typeof data.workorder === "object" ? data.workorder : null;
    const partDetail = data?.product && typeof data.product === "object" ? data.product : null;
    const hasSingleDetail = Boolean(orderDetail || caseDetail || woDetail || partDetail);

    const dataTableRaw = resolveDataTable(data);
    const dataTable =
      !dataTableRaw || hasSingleDetail || (hasRequestBuckets && (dataTableRaw.key === "products" || dataTableRaw.key === "parts"))
        ? null
        : dataTableRaw;

    // Compact label/value fact row for the single-record detail blocks.
    const factRow = (k, v) =>
      v != null && String(v).trim() !== "" ? (
        <div key={k} style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
          <strong style={{ color: "#16305c" }}>{k}:</strong> {String(v)}
        </div>
      ) : null;

    if (!hasRequestBuckets && !dataTable && !hasSingleDetail && !docs.length && !charts.length) return null;

    const byMachine = (() => {
      if (!hasRequestBuckets) return [];
      const groups = new Map();
      [...requestListRows, ...availableToPurchase].forEach((entry) => {
        const key = String(entry.machine_id || "unknown");
        const existing = groups.get(key) || {
          machine_id: entry.machine_id,
          machine_name: entry.machine_name || "Machine",
          machine_number: entry.machine_number || "N/A",
          request: [],
          available: [],
        };
        if (entry.bucket === "available") existing.available.push(entry.product);
        else existing.request.push(entry.product);
        groups.set(key, existing);
      });
      return Array.from(groups.values()).slice(0, 6);
    })();
    const wantsByMachine = /\b(by machine|machine[-\s]?wise|group(?:ed)? by machine)\b/i.test(String(item?.user || ""))
      || data?.group_by_machine === true
      || String(data?.view || "").toLowerCase() === "by_machine";

    return (
      <Box className="chat-data-block">
        {orderDetail && (
          <Box className="chat-entity-group" sx={{ mb: 1 }}>
            <Typography variant="caption" className="chat-entity-title">
              Order {orderDetail.order_number}
            </Typography>
            {orderLineItems.length > 0 && (
              <div className="chat-md-table-wrap">
                <table className="chat-md-table">
                  <thead>
                    <tr><th>Part #</th><th>Name</th><th>Qty</th><th>Line status</th></tr>
                  </thead>
                  <tbody>
                    {orderLineItems.slice(0, 25).map((p, i) => (
                      <tr key={i}>
                        <td>{p.product_number || "—"}</td>
                        <td>{p.product_name || (p.machine_name ? `(${p.machine_name})` : "—")}</td>
                        <td>{p.quantity}</td>
                        <td>{p.line_status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Box sx={{ mt: 0.5 }}>
              {[
                ["Status", orderDetail.order_status],
                ["Created", formatShortDate(orderDetail.order_created_date)],
                ["PO #", orderDetail.details?.po_number],
                ["Delivery terms", orderDetail.details?.delivery_terms],
                ["Carrier", orderDetail.details?.carrier],
                ["Ship to", orderDetail.details?.ship_to],
                ["Ship complete", orderDetail.details?.ship_complete],
                [
                  "Total",
                  Number(orderDetail.details?.order_total) > 0
                    ? `${CURRENCY_SYMBOLS[String(orderDetail.details?.currency || "").toLowerCase()] || ""}${Number(orderDetail.details.order_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : null,
                ],
                ["Notes", orderDetail.details?.order_notes],
              ]
                .filter(([, v]) => v != null && String(v).trim() !== "")
                .map(([k, v]) => (
                  <div key={k} style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                    <strong style={{ color: "#16305c" }}>{k}:</strong> {v}
                  </div>
                ))}
            </Box>
          </Box>
        )}
        {caseDetail && (
          <Box className="chat-entity-group" sx={{ mb: 1 }}>
            <Typography variant="caption" className="chat-entity-title">Case {caseDetail.case_number}</Typography>
            <Box sx={{ mt: 0.5 }}>
              {factRow("Title", caseDetail.case_title)}
              {factRow("Type", caseDetail.case_type)}
              {factRow("Status", caseDetail.case_status)}
              {factRow("Priority", caseDetail.case_priority)}
              {factRow("Created", formatShortDate(caseDetail.created_date))}
              {factRow("Closed", caseDetail.closed_date ? formatShortDate(caseDetail.closed_date) : null)}
              {factRow("Description", caseDetail.case_description || caseDetail.description)}
            </Box>
          </Box>
        )}
        {woDetail && (
          <Box className="chat-entity-group" sx={{ mb: 1 }}>
            <Typography variant="caption" className="chat-entity-title">Work Order {woDetail.wo_number}</Typography>
            <Box sx={{ mt: 0.5 }}>
              {factRow("Type", woDetail.wo_type)}
              {factRow("Status", woDetail.wo_status)}
              {factRow("Machine", woDetail.machine_name || woDetail.machine_number)}
              {factRow("Technician", woDetail.booked_resource)}
              {factRow("Service start", formatShortDate(woDetail.service_start_date))}
              {factRow("Service end", formatShortDate(woDetail.service_end_date))}
              {factRow("Description", woDetail.wo_description)}
            </Box>
          </Box>
        )}
        {partDetail && (
          <Box className="chat-entity-group" sx={{ mb: 1 }}>
            <Typography variant="caption" className="chat-entity-title">Part {partDetail.product_number}</Typography>
            <Box sx={{ mt: 0.5 }}>
              {factRow("Name", partDetail.product_name)}
              {factRow(
                "Purchasable",
                partDetail.purchasable === undefined || partDetail.purchasable === null
                  ? null
                  : Number(partDetail.purchasable) === 1 || partDetail.purchasable === true
                  ? "Cart"
                  : "Request",
              )}
              {(() => {
                const pp = formatPartPrice(partDetail, String(selectedSiteData?.site_currency || "usd").toLowerCase());
                return factRow("Price", pp === "—" ? null : pp);
              })()}
              {factRow("Machine", partDetail.machine_name)}
              {factRow("Description", partDetail.product_description)}
            </Box>
          </Box>
        )}
        {hasRequestBuckets && wantsByMachine && byMachine.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">By Machine</Typography>
            {byMachine.map((group, idx) => (
              <Box key={`chat_req_machine_${group.machine_id || idx}`} className="chat-entity-subgroup">
                <MachineListItem
                  machine={{
                    machine_id: group.machine_id,
                    machine_name: group.machine_name,
                    machine_number: group.machine_number,
                  }}
                />
                {group.request.length > 0 && (
                  <Box sx={{ pl: 1, pt: 0.5 }}>
                    <Typography variant="caption" className="chat-entity-title">Request List</Typography>
                    {group.request.slice(0, 5).map((p, pIdx) => (
                      <TopProductListItem
                        key={`chat_req_item_${group.machine_id || idx}_${p.product_id || pIdx}`}
                        product={p}
                        machine_id={group.machine_id || p.machine_id || data?.machine_id || 1}
                        source="search_results"
                      />
                    ))}
                  </Box>
                )}
                {group.available.length > 0 && (
                  <Box sx={{ pl: 1, pt: 0.5 }}>
                    <Typography variant="caption" className="chat-entity-title">Available to Purchase</Typography>
                    {group.available.slice(0, 5).map((p, pIdx) => (
                      <TopProductListItem
                        key={`chat_atp_item_${group.machine_id || idx}_${p.product_id || pIdx}`}
                        product={p}
                        machine_id={group.machine_id || p.machine_id || data?.machine_id || 1}
                        source="search_results"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {hasRequestBuckets && !wantsByMachine && (
          <>
            {requestListRows.length > 0 && (
              <Box className="chat-entity-group">
                <Typography variant="caption" className="chat-entity-title">Request List</Typography>
                {requestListRows.slice(0, 10).map((entry, idx) => (
                  <TopProductListItem
                    key={`chat_req_flat_${entry.product?.product_id || idx}`}
                    product={entry.product}
                    machine_id={entry.machine_id || entry.product?.machine_id || data?.machine_id || 1}
                    source="search_results"
                  />
                ))}
              </Box>
            )}
            {availableToPurchase.length > 0 && (
              <Box className="chat-entity-group">
                <Typography variant="caption" className="chat-entity-title">Available to Purchase</Typography>
                {availableToPurchase.slice(0, 10).map((entry, idx) => (
                  <TopProductListItem
                    key={`chat_atp_flat_${entry.product?.product_id || idx}`}
                    product={entry.product}
                    machine_id={entry.machine_id || entry.product?.machine_id || data?.machine_id || 1}
                    source="search_results"
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {dataTable && (
          <Box sx={{ mb: 1 }}>
            {dataTable.title && (
              <Typography variant="caption" className="chat-entity-title">{dataTable.title}</Typography>
            )}
            <div className="chat-md-table-wrap">
              <table className="chat-md-table">
                <thead>
                  <tr>
                    {dataTable.columns.map((col) => (
                      <th key={col.label}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataTable.rows.slice(0, 25).map((row, ri) => {
                    // Live-reported 2026-07-21 (#23/#28): on an ambiguous result the reply
                    // renders BOTH intent-aware chips ("open the model for machine 160272") and
                    // this table, whose row click sends a generic, intent-blind
                    // "Show details for machine X" — so clicking the row (the obvious target)
                    // discarded the "open model" intent and just showed details. When the turn
                    // carries actions that reference this row's identifier, reuse that action's
                    // prompt so the row does exactly what its matching chip does; otherwise keep
                    // the generic rowPrompt.
                    //
                    // rowId must be the row's OWN identity, keyed off which table this actually is
                    // (dataTable.key) — NOT a blind OR-chain across every possible id field. Live-
                    // reported 2026-07-28: clicking a PART row in a machine-scoped Parts table
                    // injected "Show purchasable parts for machine 210416" instead of "Show me part
                    // <product_number>". Root cause: every part row ALSO carries machine_number (the
                    // machine it belongs to, a foreign key — not the row's own identity), and the old
                    // chain checked machine_number BEFORE product_number, so rowId became "210416" for
                    // every part row. That coincidentally matched the UNRELATED "...for machine
                    // 210416" suggested-action chip (its prompt/label contains "210416" too), which
                    // then won over the correct, row-specific rowPrompt. A machine ROW genuinely
                    // should match on machine_number (that's ITS identity, the original 2026-07-21
                    // fix this guards) — a part row must not, since machine_number there is a parent
                    // reference shared by every row in the table. Field map lives at module scope
                    // (ROW_ID_FIELD_BY_TABLE_KEY, near DATA_TABLE_SPECS) so it isn't rebuilt per row.
                    const rowId = ROW_ID_FIELD_BY_TABLE_KEY[dataTable.key]?.(row) || null;
                    const matchingAction = rowId && Array.isArray(item?.actions)
                      ? item.actions.find((a) => {
                          const idStr = String(rowId);
                          return String(a?.prompt || "").includes(idStr) || String(a?.label || "").includes(idStr);
                        })
                      : null;
                    const prompt = (matchingAction && matchingAction.prompt)
                      || (dataTable.rowPrompt ? dataTable.rowPrompt(row) : null);
                    const rawHref = dataTable.rowHref ? dataTable.rowHref(row) : null;
                    // Only ever open http(s) URLs in a new tab — guard against a javascript:/data:
                    // scheme ever reaching window.open (the href is a backend blob URL, but validate
                    // the sink regardless). A rowHref (citation source PDF) takes precedence over a
                    // rowPrompt (which re-sends a chat message).
                    const href = typeof rawHref === "string" && /^https?:\/\//i.test(rawHref) ? rawHref : null;
                    const onRowActivate = href
                      ? () => window.open(href, "_blank", "noopener,noreferrer")
                      : (prompt ? () => handleActionClick({ prompt }) : null);
                    const clickable = Boolean(onRowActivate);
                    return (
                      <tr
                        key={ri}
                        className={clickable ? "chat-md-table-row-clickable" : undefined}
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        onClick={onRowActivate || undefined}
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onRowActivate();
                                }
                              }
                            : undefined
                        }
                      >
                        {dataTable.columns.map((col) => (
                          <td key={col.label}>{formatCell(col.get(row, { currency: String(selectedSiteData?.site_currency || "usd").toLowerCase() }))}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Box>
        )}

        {docs.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Documents</Typography>
            {docs.map((doc, idx) => (
              // onClickCapture on a wrapper (not a DocCard prop) so the shared DocCard
              // component stays untouched and the link's own behavior is unchanged.
              <Box
                key={`chat_doc_${doc.id || idx}`}
                onClickCapture={() => trackChipUx("chip_doc_opened", {
                  document_id: doc?.id ?? doc?.document_id ?? null,
                  name: String(doc?.name || doc?.document_title || "").slice(0, 120),
                  // category feeds the dashboard's "document opens by category" KPI
                  category: String(doc?.category || doc?.document_category || "").slice(0, 80) || undefined,
                }, conversationId)}
              >
                <DocCard
                  title={doc?.name || doc?.document_title || `Document ${idx + 1}`}
                  subtitle={doc?.description || doc?.document_description || ""}
                  href={getDocHref(doc)}
                  chipLabel={getDocChipLabel(doc)}
                />
              </Box>
            ))}
          </Box>
        )}

        {charts.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Analytics</Typography>
            {charts.slice(0, 3).map((chart, idx) => (
              <Box key={`chat_chart_${chart.id || idx}`} className="chat-chart-card">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {chart?.title || `Chart ${idx + 1}`}
                </Typography>
                <ReactECharts option={getChartOption(chart)} style={{ height: 220, width: "100%" }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className="chatbot-shell">
      <Box className="chatbot-header">
        <Box className="chip-brand">
          <img src={CHIP_ICON} alt="CHIP" className="chip-header-icon" />
          <Typography variant="subtitle1" className="chatbot-title">
            {t("CHAT_SUPPORT.ASK_CHIP", "Ask CHIP")}
          </Typography>
          <Chip label="AI" size="small" className="chip-header-badge" />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={chatEnabled ? t("CHAT_SUPPORT.CLEAR_CONVERSATION", "Clear conversation") : t("CHAT_SUPPORT.DISCONNECTED", "Disconnected")}>
            <span>
              <IconButton
                size="small"
                onClick={handleClearResults}
                disabled={!chatEnabled || isAnalyzing}
                className="chatbot-header-icon-btn"
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {onToggleFullPage && (
            <Tooltip title={fullPage ? t("CHAT_SUPPORT.EXIT_FULL_PAGE", "Exit full page") : t("CHAT_SUPPORT.FULL_PAGE", "Full page")}>
              <IconButton size="small" onClick={onToggleFullPage} className="chatbot-header-icon-btn">
                {fullPage ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title={t("CHAT_SUPPORT.MINIMIZE", "Minimize")}>
              <IconButton size="small" onClick={onClose} className="chatbot-header-icon-btn">
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box className="chatbot-scroll" ref={chatboxRef}>
        {showSuggestions && (
          <Box sx={{ textAlign: "center", pt: 6, px: 3 }}>
            <AutoAwesomeIcon sx={{ fontSize: 44, color: "#aaa", mb: 1.5 }} />
            <Typography variant="h3" sx={{ color: "#181818", fontSize: "18px", fontWeight: 600, mb: 3 }}>
              {t("CHAT_SUPPORT.HOW_CAN_I_HELP_YOU_TODAY")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "center", gap: "8px" }}>
              {initialQuestions.map((quest) => (
                <Button
                  key={quest}
                  className="quick-question"
                  variant="outlined"
                  size="small"
                  onClick={() => handleSuggestedQuestion(quest)}
                  startIcon={getIconForPrompt(quest, "question")}
                >
                  {quest}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {conversationHistory.length > 0 && (
          <Box sx={{ px: 1.5, pb: 2 }}>
            {conversationHistory.map((item, index) => (
              <Box
                key={item?.timestamp || item?.auditId || `chat-turn-${index}`}
                ref={(el) => { turnRefs.current[index] = el; }}
                sx={{ mb: 3 }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box sx={{ bgcolor: "primary.main", color: "white", p: 1.5, borderRadius: "16px 16px 0 16px", maxWidth: "75%" }}>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{item.user}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem", fontStyle: "italic", mt: 0.75, mr: 1 }}>
                    {formatChatTimestamp(item.timestamp)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                  {item.response === null ? (
                    <Box className="chip-stage">
                      <img src={CHIP_ICON} alt="CHIP" className="chip-avatar" />
                      <Box className="chip-stage-card" sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ animation: `${pulse} 1.4s ease-in-out infinite`, mb: 0.75 }}>
                          {tStage(stageIndex, t)}
                        </Typography>
                        <Box className="chip-thinking-shimmer">
                          <span />
                          <span />
                          <span />
                        </Box>
                      </Box>
                    </Box>
                  ) : item.response ? (
                    <Box sx={{ display: "flex", alignItems: "flex-start", columnGap: 1.2, width: "100%" }}>
                      <img src={CHIP_ICON} alt="CHIP" className="chip-avatar" />
                      <Box className={`assistant-message-wrap ${isAnalyzing && index === conversationHistory.length - 1 ? "assistant-message-typing" : ""}`} sx={{ flex: 1 }}>
                        <ChatMarkdown>{item.response}</ChatMarkdown>
                        {item.pendingAction && (
                          <Typography variant="caption" sx={{ color: "warning.main", display: "block", mt: 1 }}>
                            {t("CHAT_SUPPORT.WORKING_ON", "Working on:")} {item.pendingAction}
                          </Typography>
                        )}
                        {renderDataBlocks(item)}
                        {Array.isArray(item.actions) && item.actions.length > 0 && (
                          <>
                            <Divider sx={{ mt: 1, mb: 0.75 }} />
                            {(() => {
                              const actions = item.actions.slice(0, 6);
                              const questions = actions.filter((act) => inferActionType(act) === "question");
                              const agentic = actions.filter((act) => inferActionType(act) === "agentic");
                              if (!questions.length && !agentic.length) return null;
                              // Action chips render on every historical turn, but only the most
                              // recent turn's actions are still relevant — e.g. a "Place order" or
                              // "Add to cart" chip from several messages back stays clickable
                              // forever otherwise, letting a stale action fire against a
                              // conversation state it no longer matches. Once a newer turn exists,
                              // gray out (and stop wiring up) every earlier turn's chips instead of
                              // expiring them on a timer.
                              const isLatestTurn = index === conversationHistory.length - 1;
                              const staleTitle = t(
                                "CHAT_SUPPORT.STALE_ACTION_TITLE",
                                "This action is from an earlier turn — ask again to get a current one.",
                              );
                              return (
                                <Box className="chat-actions-wrap">
                                  {questions.length > 0 && (
                                    <Box className="chat-actions-group chat-actions-group-question">
                                      <Box className="chat-actions-head">
                                        <Typography variant="caption" className="chat-actions-title chat-actions-title-question">
                                          <HelpOutlineIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
                                          {t("CHAT_SUPPORT.ASK_GROUP_LABEL", "Ask")}
                                        </Typography>
                                      </Box>
                                      <Box className="chat-actions-row">
                                        {questions.map((act, idx) => (
                                          <Button
                                            key={`${act.label || "question"}_q_${idx}`}
                                            size="small"
                                            variant="outlined"
                                            className="chat-action-chip chat-action-chip-question"
                                            disabled={!isLatestTurn}
                                            title={isLatestTurn ? undefined : staleTitle}
                                            onClick={() => handleActionClick(act)}
                                            startIcon={getIconForPrompt(act.prompt || act.label, "question")}
                                          >
                                            {act.label || act.prompt || t("CHAT_SUPPORT.QUESTION_FALLBACK", "Question")}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {agentic.length > 0 && (
                                    <Box className="chat-actions-group chat-actions-group-agentic">
                                      <Box className="chat-actions-head">
                                        <Typography variant="caption" className="chat-actions-title chat-actions-title-agentic">
                                          <BoltIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
                                          {t("CHAT_SUPPORT.SHORTCUTS_GROUP_LABEL", "Shortcuts")}
                                        </Typography>
                                      </Box>
                                      <Box className="chat-actions-row">
                                        {agentic.map((act, idx) => (
                                          <Button
                                            key={`${act.label || "action"}_a_${idx}`}
                                            size="small"
                                            variant="outlined"
                                            className="chat-action-chip chat-action-chip-agentic"
                                            disabled={!isLatestTurn}
                                            title={isLatestTurn ? undefined : staleTitle}
                                            onClick={() => handleActionClick(act)}
                                            startIcon={getIconForPrompt(act.prompt || act.label, "agentic")}
                                          >
                                            {act.label || act.prompt || t("CHAT_SUPPORT.ACTION_FALLBACK", "Action")}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}
                          </>
                        )}
                        {item.auditId && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
                            <IconButton
                              size="small"
                              color={item.feedback === "helpful" || item.pendingFeedback === "helpful" ? "success" : "default"}
                              disabled={Boolean(item.feedback) || Boolean(item.pendingFeedback)}
                              onClick={() => handleResponseFeedback(index, "helpful")}
                              title={t("CHAT_SUPPORT.HELPFUL", "Helpful")}
                              sx={{
                                color: item.feedback === "helpful" || item.pendingFeedback === "helpful" ? "success.dark" : "inherit",
                                opacity: item.feedback === "not_helpful" || item.pendingFeedback === "not_helpful" ? 0.55 : 1,
                                transition: "all 0.18s ease",
                              }}
                            >
                              {item.feedback === "helpful" || item.pendingFeedback === "helpful" ? <FaThumbsUp size={14} /> : <FaRegThumbsUp size={14} />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color={item.feedback === "not_helpful" || item.pendingFeedback === "not_helpful" ? "error" : "default"}
                              disabled={Boolean(item.feedback) || Boolean(item.pendingFeedback)}
                              onClick={() => handleResponseFeedback(index, "not_helpful")}
                              title={t("CHAT_SUPPORT.NOT_HELPFUL", "Not Helpful")}
                              sx={{
                                color: item.feedback === "not_helpful" || item.pendingFeedback === "not_helpful" ? "error.dark" : "inherit",
                                opacity: item.feedback === "helpful" || item.pendingFeedback === "helpful" ? 0.55 : 1,
                                transition: "all 0.18s ease",
                              }}
                            >
                              {item.feedback === "not_helpful" || item.pendingFeedback === "not_helpful" ? <FaThumbsDown size={14} /> : <FaRegThumbsDown size={14} />}
                            </IconButton>
                            {item.pendingFeedback && (
                              <Box key={`undo-${index}-${item.pendingFeedback}`} className="chatbot-feedback-undo">
                                <Typography
                                  variant="caption"
                                  className="chatbot-feedback-undo-link"
                                  onClick={() => handleUndoFeedback(index)}
                                >
                                  {t("CHAT_SUPPORT.UNDO", "Undo")}
                                </Typography>
                                <Box className="chatbot-feedback-undo-bar">
                                  <Box className="chatbot-feedback-undo-bar-fill" />
                                </Box>
                              </Box>
                            )}
                            {item.feedbackError && <Typography variant="caption" color="error">{item.feedbackError}</Typography>}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="error">
                      {t("ERRORS.ERROR_GENERATING_RESPONSE_PLEASE_TRY_AGAIN")}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box className="chatbot-composer">
        {!chatEnabled && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1, display: "block", mb: 0.5 }}>
            {t("CHAT_SUPPORT.ASSISTANT_UNAVAILABLE", "Assistant is temporarily unavailable. Try again shortly.")}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("CHAT_SUPPORT.CHATBOT_PLACEHOLDER", "Ask about orders, parts, documents, or your cart")}
            multiline
            maxRows={3}
            size="small"
            fullWidth
            disabled={!chatEnabled || isAnalyzing}
            className="chatbot-input-field"
            inputProps={{ maxLength: 4000 }}
          />
          <Tooltip title={isAnalyzing ? t("CHAT_SUPPORT.STOP_GENERATING", "Stop generating") : t("CHAT_SUPPORT.SEND", "Send")} arrow>
            <span>
              <IconButton
                onClick={isAnalyzing ? handleStopStreaming : () => {
                  sendSourceRef.current = "typed";
                  setSendMessage(true);
                }}
                disabled={isAnalyzing ? false : (!input.trim() || !chatEnabled)}
                className={`chatbot-send-btn ${isAnalyzing ? "chatbot-send-btn-stop" : ""}`}
              >
                {isAnalyzing ? <StopIcon sx={{ fontSize: 18 }} /> : <SendIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {input.length > 3500 && (
          <Typography
            variant="caption"
            color={input.length >= 4000 ? "error" : "text.secondary"}
            sx={{ display: "block", textAlign: "right", pr: 1, mt: 0.25 }}
          >
            {input.length} / 4000
          </Typography>
        )}
      </Box>

      {/* Same in-app viewer the Documentation page uses. Rendered inside the chat so opening a
          cited PDF never leaves the conversation, and never needs a pop-up or a new tab. */}
      {docModal.open && (
        <DocumentViewModal
          open={docModal.open}
          setOpen={(v) => setDocModal((m) => ({ ...m, open: Boolean(v) }))}
          document={docModal.document}
          onClose={() => setDocModal({ open: false, document: null, page: null })}
        />
      )}
    </Box>
  );
};

export default ChatBot;
